import { Dialect } from '../dialect.js';
import { normalizeKeywordText } from '../lexer/keywords.js';
import { printExpression } from '../printer/printer.js';
import type {
  ArrayAccess,
  AssignmentStatement,
  BinaryExpression,
  CallStatement,
  Declaration,
  DeclaredType,
  Expression,
  ForStatement,
  FunctionCall,
  Identifier,
  IfStatement,
  LValue,
  Program,
  ReadStatement,
  RepeatUntilStatement,
  ReturnType,
  Statement,
  Subprogram,
  SwapStatement,
  SwitchCaseValue,
  SwitchStatement,
  UnaryExpression,
  WhileStatement,
  WriteStatement,
} from '../parser/ast.js';
import { detectArrayArity } from './detect-array-usage.js';
import { Environment } from './environment.js';
import type { InterpreterIO } from './io.js';
import { RuntimeArray } from './runtime-array.js';
import { RuntimeError } from './runtime-error.js';
import { describeRuntimeValue, formatRuntimeValue } from './runtime-value.js';
import type { RuntimeValue } from './runtime-value.js';

export interface InterpretResult {
  readonly error: RuntimeError | null;
  readonly cancelled: boolean;
}

/** Εσωτερικό σήμα ελέγχου ροής — όχι σφάλμα, δεν εκτίθεται στον καλούντα ως exception. */
class Cancelled extends Error {}

/**
 * Προεπιλεγμένο ανώτατο όριο "βημάτων" (μία είσοδος σε `executeStatements` —
 * δηλαδή μία επανάληψη ΓΙΑ/ΟΣΟ/ΜΕΧΡΙΣ_ΟΤΟΥ, ή μία είσοδος σε σώμα ΑΝ/
 * υποπρογράμματος) πριν ο διερμηνευτής υποθέσει άτερμο βρόχο και σταματήσει
 * μόνος του με σαφές μήνυμα — βλ. `checkCancellationAndYield`. Βασισμένο σε
 * αριθμό βημάτων (όχι πραγματικό χρόνο) ώστε να ΜΗΝ επηρεάζεται από την
 * "αργή εκτέλεση" (`delayMs`) — ένας μαθητής που παρακολουθεί βήμα-βήμα ένα
 * αργό αλλά νόμιμο πρόγραμμα δεν πρέπει ποτέ να χτυπήσει το όριο. Αρκετά
 * μεγάλο ώστε να μην ενοχλεί νόμιμους βαρείς υπολογισμούς (π.χ. εμφωλευμένοι
 * βρόχοι σε μεγάλους πίνακες), αρκετά μικρό ώστε ένας πραγματικά άτερμος
 * βρόχος να μη γεμίζει το tab επ' άπειρον απαρατήρητος. Παραμετροποιήσιμο
 * (βλ. `run`/`runStatements`) κυρίως για να μπορούν τα tests να το ορίζουν
 * μικρό και να μένουν γρήγορα.
 */
const DEFAULT_MAX_LOOP_STEPS = 2_000_000;

/**
 * Πόσο συχνά (πραγματικός χρόνος τοίχου, όχι "βήματα") επιτρέπεται στο UI
 * (web) να ζωγραφίσει ενδιάμεση πρόοδο — βλ. `maybeYieldToUI`. Χωρίς αυτό,
 * ένας σφιχτός βρόχος με ΔΙΑΒΑΣΕ που απαντιέται αυτόματα (π.χ. "🎲 Τυχαία
 * τιμή"/"Συμπλήρωσε τον πίνακα", βλ. web/main.ts `activeArrayFills`) τρέχει
 * ΟΛΕΣ τις επαναλήψεις σε μία αδιάκοπη αλυσίδα microtask, χωρίς ΠΟΤΕ να
 * δώσει την ευκαιρία στον browser να ζωγραφίσει (το `requestAnimationFrame`
 * του `scheduleLineHighlight` απλά δεν προλαβαίνει να "πυροδοτηθεί" καθόλου)
 * — η υπογράμμιση τρέχουσας γραμμής/το διάγραμμα ροής φαίνονται "παγωμένα"
 * σε όλη τη διάρκεια, αντί να δείχνουν την πρόοδο (bug report). Πολύ μικρό
 * ώστε να ΜΗΝ επιβραδύνει αισθητά σύντομα προγράμματα (που τελειώνουν πριν
 * καν φτάσουν το όριο, οπότε ΔΕΝ γίνεται ποτέ καμία επιπλέον απόδοση ελέγχου).
 */
const UI_YIELD_INTERVAL_MS = 30;

/** Μοίρες → ακτίνια — οι τριγωνομετρικές "Έτοιμες συναρτήσεις" του βιβλίου (ΗΜ/ΣΥΝ/ΕΦ) παίρνουν όρισμα σε ΜΟΙΡΕΣ, όχι ακτίνια. */
function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Οι "Έτοιμες συναρτήσεις" του σχολικού βιβλίου (πίνακας μαθηματικών
 * συναρτήσεων) — όλες παίρνουν ΑΚΡΙΒΩΣ 1 αριθμητικό όρισμα (οι
 * τριγωνομετρικές σε ΜΟΙΡΕΣ, βλ. `degreesToRadians`). Τα ονόματα ταιριάζουν
 * case/τόνο-ανεξάρτητα (ίδια λογική με `normalizeKeywordText` των λέξεων-
 * κλειδιών) ΑΛΛΑ ΔΕΝ είναι δεσμευμένες λέξεις — δεν εμποδίζουν μεταβλητή με
 * το ίδιο όνομα, μόνο η σύνταξη κλήσης "Όνομα(...)" τις αναγνωρίζει ως
 * συνάρτηση. Κοινές και στις δύο διαλέκτους (ΓΛΩΣΣΑ/Ψευδογλώσσα) — δεν
 * είναι λέξη-κλειδί άρα δεν έχει "μορφή εμφάνισης" ανά διάλεκτο, γράφονται
 * πάντα όπως στο βιβλίο.
 */
const BUILTIN_MATH_FUNCTIONS: Record<string, (x: number) => number> = {
  ΗΜ: (x) => Math.sin(degreesToRadians(x)),
  ΣΥΝ: (x) => Math.cos(degreesToRadians(x)),
  ΕΦ: (x) => Math.tan(degreesToRadians(x)),
  ΛΟΓ: Math.log,
  Ε: Math.exp,
  Α_Μ: Math.trunc,
  Α_Τ: Math.abs,
  Τ_Ρ: Math.sqrt,
};

/** Ονόματα των έτοιμων μαθηματικών συναρτήσεων παραπάνω — χρήσιμο αλλού για να ΞΕΧΩΡΙΣΤΟΥΝ από κλήσεις σε (πιθανώς μελλοντικές) ΣΥΝΑΡΤΗΣΕΙΣ του μαθητή (βλ. web `codeSuggestsSubprogram`). */
export const BUILTIN_MATH_FUNCTION_NAMES: ReadonlySet<string> = new Set(Object.keys(BUILTIN_MATH_FUNCTIONS));

/**
 * Οι έτοιμες συναρτήσεις είναι δεσμευμένες ως ονόματα μεταβλητών/πινάκων/
 * σταθερών/παραμέτρων: ΗΜ, ΣΥΝ, ΕΦ, ΛΟΓ, Ε, Α_Μ, Α_Τ, Τ_Ρ (βιβλίο, ενότητα 7.3).
 */
export const RESERVED_FUNCTION_IDENTIFIERS: ReadonlySet<string> = BUILTIN_MATH_FUNCTION_NAMES;

/** Ενικός (τύπος επιστροφής ΣΥΝΑΡΤΗΣΗΣ) → πληθυντικός (DeclaredType) — ώστε το `checkDeclaredType` να ελέγχει και την "όνομα ← έκφραση" μέσα στη συνάρτηση με το ΙΔΙΟ μηχανισμό όπως μια κανονική δηλωμένη μεταβλητή. */
const RETURN_TYPE_TO_DECLARED_TYPE: Record<ReturnType, DeclaredType> = {
  ΑΚΕΡΑΙΑ: 'ΑΚΕΡΑΙΕΣ',
  ΠΡΑΓΜΑΤΙΚΗ: 'ΠΡΑΓΜΑΤΙΚΕΣ',
  ΧΑΡΑΚΤΗΡΑΣ: 'ΧΑΡΑΚΤΗΡΕΣ',
  ΛΟΓΙΚΗ: 'ΛΟΓΙΚΕΣ',
};

/**
 * Διερμηνευτής βασικών εντολών + πινάκων + υποπρογραμμάτων (tree-walking).
 *
 * Τα υποπρογράμματα (ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ, μόνο ΓΛΩΣΣΑ — βλ. `callSubprogram`)
 * έχουν ΑΥΣΤΗΡΑ τοπική εμβέλεια μεταβλητών: καμία πρόσβαση σε μεταβλητές
 * του καλούντος εκτός από τις παραμέτρους τους. Η μεταβίβαση παραμέτρων
 * είναι "copy-in/copy-out" (τιμή προς τα μέσα κατά την κλήση, τελική τιμή
 * προς τα έξω κατά την επιστροφή — ΟΧΙ ζωντανή αναφορά καθ' όλη τη
 * διάρκεια), ακριβώς όπως το ιχνηλατεί το σχολικό βιβλίο.
 *
 * Σταματάει στο πρώτο runtime error (τα `ΓΡΑΨΕ` που έγιναν ήδη, έγιναν —
 * δεν έχει νόημα "συλλογή πολλαπλών σφαλμάτων" όπως στον Lexer/Parser).
 */
/**
 * Ποιό scope μεταβλητών είναι ενεργό ΑΥΤΗ ΤΗ ΣΤΙΓΜΗ — 'main' για το κύριο
 * πρόγραμμα, 'procedure'/'function' με το ΟΝΟΜΑ του συγκεκριμένου
 * υποπρογράμματος όσο εκτελείται το σώμα του (βλ. `callSubprogram`). Ο
 * σκοπός είναι να μπορεί ένα UI (π.χ. web/src/main.ts) να δείξει τον
 * "πίνακα τιμών" του βιβλίου — ξεχωριστές στήλες ανά scope.
 */
export type VariableScope = { readonly kind: 'main' } | { readonly kind: 'procedure' | 'function'; readonly name: string };

/**
 * @param lastCondition Η ΠΙΟ ΠΡΟΣΦΑΤΗ αξιολόγηση συνθήκης βρόχου (ΓΙΑ/ΟΣΟ/
 *   ΜΕΧΡΙΣ_ΟΤΟΥ) — `text` είναι η ίδια η συνθήκη ξαναγραμμένη σε πηγαίο κώδικα
 *   (π.χ. "ι <= 3", βλ. `printExpression`· για ΓΙΑ συντίθεται η ισοδύναμη
 *   σύγκριση του ΟΣΟ-μοντέλου, βλ. `executeFor`) ώστε το web UI να δείχνει
 *   ΠΟΙΑ ακριβώς συνθήκη ελέγχθηκε — κρίσιμο σε εμφωλευμένους βρόχους, όπου
 *   μια γυμνή Αληθής/Ψευδής δεν λέει ΠΟΙΟΥ βρόχου είναι ο έλεγχος. `null` αν η
 *   αναφορά μεταβλητών ΔΕΝ συνδέεται με έλεγχο συνθήκης (π.χ. μετά από απλή
 *   ανάθεση). "Μιας χρήσης": μηδενίζεται αμέσως μετά την πρώτη αναφορά που το
 *   καταναλώνει (βλ. `reportVariables`), ώστε να εμφανίζεται ΜΟΝΟ στη γραμμή
 *   του "πίνακα τιμών" (web UI) που πράγματι αντιστοιχεί σε αυτόν τον έλεγχο.
 * @param lastArrayWrite Μη-`null` αν αυτή η αναφορά προέρχεται από εγγραφή σε
 *   ΜΕΜΟΝΩΜΕΝΟ(Α) στοιχείο(α) πίνακα (ΔΙΑΒΑΣΕ/ανάθεση/ΑΝΤΑΛΛΑΓΗ σε
 *   `Α[δείκτες]`) — `arrayNames` είναι μόνο τα ΟΝΟΜΑΤΑ των πινάκων (π.χ.
 *   ["Α"], ή ["Α","Β"] αν ΔΥΟ διαφορετικοί πίνακες γράφτηκαν στην ΙΔΙΑ
 *   εντολή, π.χ. "ΔΙΑΒΑΣΕ Α[i], Β[i]" — bug report: με μονό όνομα (αντί για
 *   λίστα) φαινόταν μόνο ο ΤΕΛΕΥΤΑΙΟΣ πίνακας που γράφτηκε, σαν ο πρώτος να
 *   μην είχε γραφτεί καθόλου), ΧΩΡΙΣ δείκτες/τιμές (βλ. `assignToLValue`). Ο
 *   σκοπός ΔΕΝ είναι να δείξει ΤΙ γράφτηκε (θα χρειαζόταν ιστορικό ολόκληρου
 *   του πίνακα, απαγορευτικό για μεγάλους πίνακες), αλλά ΜΟΝΟ να επισημάνει
 *   ΟΤΙ έγινε εγγραφή σε πίνακα σε αυτή τη γραμμή — αρχικό bug report: μια
 *   τέτοια γραμμή έμοιαζε με άσκοπο διπλότυπο της προηγούμενης, αφού καμία
 *   ΟΡΑΤΗ (βαθμωτή) μεταβλητή δεν άλλαξε. ΙΔΙΟ μοντέλο "μιας χρήσης" με το
 *   `lastCondition`, απλά συσσωρεύει ΟΛΑ τα ονόματα αντί να κρατάει μόνο το
 *   τελευταίο. `null` αν η αναφορά μεταβλητών ΔΕΝ συνδέεται με εγγραφή σε πίνακα.
 */
export type VariablesListener = (
  vars: ReadonlyMap<string, RuntimeValue>,
  scope: VariableScope,
  lastCondition: { readonly result: boolean; readonly text: string } | null,
  lastArrayWrite: { readonly arrayNames: readonly string[] } | null,
) => void;

/** Καλείται με τη γραμμή πηγαίου κώδικα της εντολής που πρόκειται να εκτελεστεί — για υπογράμμιση "τρέχουσας εντολής" σε web UI. */
export type StatementListener = (line: number) => void;

/**
 * Καλείται με τη γραμμή ΚΑΘΕ εντολής, ΜΕΤΑ την εκτέλεσή της (βλ.
 * `executeStatements`) — για breakpoints σε web UI. Επίτηδες ΜΕΤΑ (όχι πριν):
 * ο μαθητής θέλει να βλέπει το ΑΠΟΤΕΛΕΣΜΑ της εντολής όπου έβαλε την τελίτσα
 * (π.χ. τιμή που μόλις διαβάστηκε) πριν παγώσει, όχι να σταματήσει λίγο πριν
 * αυτή προλάβει να τρέξει. Αν επιστρέψει `undefined`, η εκτέλεση συνεχίζει
 * αμέσως (η γραμμή δεν έχει breakpoint). Αν επιστρέψει `Promise<void>`, η
 * εκτέλεση ΠΕΡΙΜΕΝΕΙ να λυθεί (π.χ. μέχρι να πατηθεί "Συνέχεια") — ο ίδιος ο
 * interpreter δεν ξέρει ΤΙΠΟΤΑ για "ποιες γραμμές έχουν breakpoint", απλά
 * αναθέτει την απόφαση εδώ (ίδιο σκεπτικό με το `InterpreterIO.input()` — το
 * web UI αποφασίζει, ο interpreter απλά περιμένει).
 */
export type BreakpointHook = (line: number) => void | Promise<void>;

const MAIN_SCOPE: VariableScope = { kind: 'main' };

export class Interpreter {
  private env = new Environment();
  private readonly io: InterpreterIO;
  private signal: AbortSignal | undefined;
  private onVariablesChanged: VariablesListener | undefined;
  private onStatementExecuting: StatementListener | undefined;
  private onBreakpoint: BreakpointHook | undefined;
  private stepCount = 0;
  private maxSteps: number = DEFAULT_MAX_LOOP_STEPS;
  private delayMs = 0;
  /** Πότε δόθηκε τελευταία φορά ευκαιρία στο UI να ζωγραφίσει — βλ. `maybeYieldToUI`/`UI_YIELD_INTERVAL_MS`. */
  private lastUiYieldAt = Date.now();
  /** Βλ. `VariablesListener` — αποτέλεσμα (+ κείμενο) του πιο πρόσφατου, ΜΗ ΑΚΟΜΑ αναφερμένου ελέγχου συνθήκης βρόχου. */
  private lastCondition: { result: boolean; text: string } | null = null;
  /** Βλ. `VariablesListener` — ονόματα πινάκων που γράφτηκαν (ΜΗ ΑΚΟΜΑ αναφερμένα) από την τελευταία αναφορά — `Set`, ΟΧΙ μονό όνομα, γιατί μια ΜΟΝΟ εντολή μπορεί να γράψει σε ΠΑΝΩ ΑΠΟ ΕΝΑΝ πίνακα (π.χ. "ΔΙΑΒΑΣΕ Α[i], Β[i]"). */
  private readonly lastArrayWriteNames = new Set<string>();
  /** Αυξάνεται σε ΚΑΘΕ `reportVariables()` — βλ. `executeStatements`, όπου χρησιμεύει για να ξεχωρίσει "καμία εσωτερική αναφορά δεν έγινε ακόμα" από "μια σύνθετη εντολή (ΑΝ/ΓΙΑ/ΟΣΟ/...) ήδη ανέφερε η ίδια την τελική της κατάσταση". */
  private reportSequence = 0;
  /** Όνομα → δήλωση, γεμίζει στο `run()` για προγράμματα ΓΛΩΣΣΑΣ (βλ. GlossaProgram.subprograms). */
  private readonly subprograms = new Map<string, Subprogram>();
  /** true όσο εκτελείται το σώμα μιας ΣΥΝΑΡΤΗΣΗΣ (ή οποιασδήποτε άλλης συνάρτησης που αυτή κάλεσε) — μια συνάρτηση δεν μπορεί να έχει ΔΙΑΒΑΣΕ/ΓΡΑΨΕ ούτε να καλέσει διαδικασία (βλ. βιβλίο). */
  private insideFunction = false;
  /** Ποιό scope είναι ενεργό αυτή τη στιγμή — βλ. `VariableScope`. Αλλάζει μόνο μέσα στο `callSubprogram`. */
  private currentScope: VariableScope = MAIN_SCOPE;
  /**
   * Ορίζεται στο `run()` από `program.dialect`. Μόνο η ΓΛΩΣΣΑ απαιτεί ΚΑΘΕ
   * μεταβλητή/πίνακας (ΚΑΙ ο μετρητής ενός ΓΙΑ) να έχει δηλωθεί ρητά στο
   * ΜΕΤΑΒΛΗΤΕΣ πριν χρησιμοποιηθεί — βλ. `requireDeclaredInGlossa`. Η
   * Ψευδογλώσσα παραμένει εντελώς δυναμική, γι' αυτό το προεπιλεγμένο εδώ
   * είναι PSEUDOGLOSSA (ισχύει και για "τμήμα αλγορίθμου", που δεν περνάει
   * ρητή διάλεκτο στο `runStatements` — ίδια, αμετάβλητη συμπεριφορά όπως πριν).
   */
  private dialect: Dialect = Dialect.PSEUDOGLOSSA;
  /**
   * Όνομα ΕΝΕΡΓΟΥ μετρητή ΓΙΑ → το ανώτατο όριο που θα φτάσει (βλ.
   * `inferArrayDimensionsFromLoopContext`). Γεμίζει/αδειάζει μόνο στο
   * `executeFor` (ένα per ενεργό βρόχο, υποστηρίζει και εμφωλευμένους) —
   * ξεχωριστό Map ανά κλήση υποπρογράμματος (βλ. `callSubprogram`), αφού
   * κάθε υποπρόγραμμα έχει αυστηρά τοπική εμβέλεια.
   */
  private activeForLoopBounds = new Map<string, number>();
  /**
   * Ονόματα μετρητών ΓΙΑ που είναι ΑΥΤΗ ΤΗ ΣΤΙΓΜΗ "κλειδωμένα" — ο μαθητής
   * δεν επιτρέπεται να αλλάξει χειροκίνητα την τιμή τους μέσα στο σώμα του
   * ΔΙΚΟΥ ΤΟΥΣ βρόχου (μόνο ο ίδιος ο βρόχος τους προχωράει, βλ. `executeFor`
   * και `requireNotLockedForCounter`). Ξεχωριστό Set ανά κλήση υποπρογράμματος
   * (βλ. `callSubprogram`) — ίδιο σκεπτικό με `activeForLoopBounds`.
   */
  private lockedForCounters = new Set<string>();

  constructor(io: InterpreterIO) {
    this.io = io;
  }

  /**
   * @param signal Προαιρετικό AbortSignal για διακοπή εν εξελίξει εκτέλεσης
   *   (π.χ. κουμπί "Διακοπή" σε web UI, όταν ο μαθητής έγραψε άπειρο βρόχο).
   *   Ελέγχεται πριν από κάθε ακολουθία εντολών — καλύπτει και βρόχους με
   *   κενό σώμα. Κάθε ~1000 βήματα η εκτέλεση παραχωρεί πραγματικά τον
   *   έλεγχο στο event loop (μέσω setTimeout) ώστε ένα κλικ στο "Διακοπή"
   *   να προλαβαίνει να επεξεργαστεί — αλλιώς ένας σφιχτός υπολογιστικός
   *   βρόχος θα «πάγωνε» το tab χωρίς να αφήνει τον browser να αντιδράσει.
   * @param onVariablesChanged Προαιρετικό callback που καλείται μετά από
   *   κάθε εντολή (και σε κάθε επανάληψη του ΓΙΑ) με στιγμιότυπο όλων των
   *   μεταβλητών ΤΟΥ ΤΡΕΧΟΝΤΟΣ scope, μαζί με ποιό scope είναι αυτό (βλ.
   *   `VariableScope`) — για ζωντανή εμφάνιση σε web UI (π.χ. panel
   *   μεταβλητών, "πίνακας τιμών" στυλ σχολικού βιβλίου).
   * @param onStatementExecuting Προαιρετικό callback που καλείται με τη
   *   γραμμή κάθε εντολής ΠΡΙΝ εκτελεστεί — για υπογράμμιση "τρέχουσας
   *   εντολής" στον editor σε web UI (όπως στο alkisg.mysch.gr).
   * @param delayMs Προαιρετική καθυστέρηση (σε ms) μετά από κάθε εντολή —
   *   για "αργή εκτέλεση" σε web UI, ώστε ο μαθητής να προλαβαίνει να
   *   παρακολουθήσει βήμα-βήμα την τρέχουσα γραμμή και τις μεταβλητές.
   *   Ακυρώνεται αμέσως αν ενεργοποιηθεί το signal, όπως και το `waitForInput`.
   * @param maxSteps Ανώτατο όριο βημάτων πριν αυτόματη διακοπή λόγω πιθανού
   *   άτερμου βρόχου — βλ. `DEFAULT_MAX_LOOP_STEPS`. Παραμετροποιήσιμο κυρίως
   *   για tests.
   * @param onBreakpoint Προαιρετικό callback breakpoint — βλ. `BreakpointHook`.
   */
  async run(
    program: Program,
    signal?: AbortSignal,
    onVariablesChanged?: VariablesListener,
    onStatementExecuting?: StatementListener,
    delayMs?: number,
    maxSteps?: number,
    onBreakpoint?: BreakpointHook,
  ): Promise<InterpretResult> {
    this.signal = signal;
    this.onVariablesChanged = onVariablesChanged;
    this.onStatementExecuting = onStatementExecuting;
    this.delayMs = delayMs ?? 0;
    this.maxSteps = maxSteps ?? DEFAULT_MAX_LOOP_STEPS;
    this.onBreakpoint = onBreakpoint;
    this.dialect = program.dialect;
    return this.runWithErrorHandling(async () => {
      if (program.dialect === Dialect.GLOSSA) {
        this.registerSubprograms(program.subprograms);
        await this.registerDeclarations(program.declarations);
      } else {
        await this.promptForDataVariables(program.dataVariables, program.body, program.line, program.column);
      }
      await this.executeStatements(program.body);
      if (program.dialect === Dialect.PSEUDOGLOSSA) {
        await this.printResultVariables(program.resultVariables, program.line, program.column);
      }
      // Δείχνει ΚΑΙ το ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ/ΤΕΛΟΣ στο τέλος — ίδιο σκεπτικό με
      // το ΤΕΛΟΣ_ΑΝ (βλ. `executeIf`).
      if (program.endLine !== null) {
        this.onStatementExecuting?.(program.endLine);
        await this.waitDelay();
      }
    });
  }

  /**
   * ΣΤΑΘΕΡΕΣ/ΜΕΤΑΒΛΗΤΕΣ ενός προγράμματος ΓΛΩΣΣΑΣ Ή ενός υποπρογράμματος
   * (βλ. `callSubprogram`) — καταχωρεί στο ΤΡΕΧΟΝ `this.env`. Μόνο οι πίνακες
   * δεσμεύουν χώρο/όρια εδώ — οι απλές μεταβλητές παραμένουν σκόπιμα χωρίς
   * τιμή (η χρήση τους πριν την πρώτη ανάθεση/ΔΙΑΒΑΣΕ είναι πραγματικό
   * runtime error, όπως και σε πραγματικούς μεταγλωττιστές ΓΛΩΣΣΑΣ).
   */
  /**
   * @param reservedName Το όνομα μιας ΣΥΝΑΡΤΗΣΗΣ (χρησιμοποιείται ΚΑΙ ως η
   * μεταβλητή επιστροφής της) δεν επιτρέπεται να ξαναδηλωθεί ως σταθερά,
   * μεταβλητή ή πίνακας μέσα στο ΔΙΚΟ ΤΗΣ ΜΕΤΑΒΛΗΤΕΣ/ΣΤΑΘΕΡΕΣ — θα ήταν
   * ασαφές ποιο "Χ" εννοείται. `undefined` για το κύριο πρόγραμμα και τις
   * ΔΙΑΔΙΚΑΣΙΕΣ (βλ. `callSubprogram`).
   */
  private async registerDeclarations(declarations: readonly Declaration[], reservedName?: string): Promise<void> {
    // Ονόματα ήδη δηλωμένα ΜΕΣΑ σε αυτή τη λίστα δηλώσεων (τοπικό στο
    // τρέχον scope — κύριο πρόγραμμα ή ένα υποπρόγραμμα, βλ.
    // `findDuplicateDeclaration`). Δίχτυ ασφαλείας εδώ, ΔΕΝ ελέγχει
    // παραμέτρους (η δική τους μοναδική αντιστοίχιση δηλώνεται ΜΙΑ φορά,
    // βλ. `findParameterDeclarationInfo`).
    const seenNames = new Set<string>(reservedName === undefined ? [] : [reservedName]);
    for (const decl of declarations) {
      if (decl.kind === 'ConstantDeclaration') {
        this.requireNotReservedFunctionName(decl.name, decl.line, decl.column);
        this.requireNotDuplicateDeclaration(seenNames, decl.name, decl.line, decl.column);
        this.env.define(decl.name, await this.evaluateExpression(decl.value));
        this.env.markConstant(decl.name);
        continue;
      }
      for (const declarator of decl.declarators) {
        this.requireNotReservedFunctionName(declarator.name, decl.line, decl.column);
        this.requireNotDuplicateDeclaration(seenNames, declarator.name, decl.line, decl.column);
        this.env.declareType(declarator.name, decl.declaredType);
        // Αν το όνομα έχει ΗΔΗ τιμή (πίνακας-παράμετρος που μόλις
        // αντιγράφηκε στο copy-in του `callSubprogram`, βλ.
        // `requireMatchingParameterKind`), ΜΗΝ τον ξαναδημιουργήσεις εδώ —
        // θα έσβηνες σιωπηλά τα δεδομένα που πέρασε ο καλών.
        if (declarator.dimensions.length > 0 && !this.env.has(declarator.name)) {
          const dims: number[] = [];
          for (const d of declarator.dimensions) dims.push(await this.evaluateArrayDimension(d));
          this.env.define(declarator.name, new RuntimeArray(dims));
        }
      }
    }
  }

  /** Μια έτοιμη μαθηματική συνάρτηση (ΗΜ/ΣΥΝ/ΕΦ/ΛΟΓ/Α_Μ/Α_Τ/Τ_Ρ) δεν επιτρέπεται ως όνομα (βλ. `findReservedFunctionNameAsIdentifier`)· δίχτυ ασφαλείας του static ελέγχου. */
  private requireNotReservedFunctionName(name: string, line: number, column: number): void {
    if (RESERVED_FUNCTION_IDENTIFIERS.has(normalizeKeywordText(name))) {
      throw new RuntimeError(`Η λέξη '${name}' είναι δεσμευμένη (έτοιμη μαθηματική συνάρτηση) και δεν μπορεί να χρησιμοποιηθεί ως όνομα μεταβλητής`, line, column);
    }
  }

  /** Το ίδιο όνομα δεν επιτρέπεται να δηλωθεί δύο φορές στο ΙΔΙΟ τμήμα δηλώσεων (βλ. `findDuplicateDeclaration`). */
  private requireNotDuplicateDeclaration(seenNames: Set<string>, name: string, line: number, column: number): void {
    if (seenNames.has(name)) {
      throw new RuntimeError(`Το όνομα '${name}' έχει ήδη δηλωθεί`, line, column);
    }
    seenNames.add(name);
  }

  /** Καταχωρεί ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ δηλώσεις πριν την εκτέλεση — κοινό για `run()` (GlossaProgram.subprograms) και `runStatements()` ("τμήμα αλγορίθμου" με ΚΑΛΕΣΕ, βλ. `Parser.parseFragment`). */
  private registerSubprograms(subs: readonly Subprogram[]): void {
    for (const sub of subs) {
      if (this.subprograms.has(sub.name)) {
        throw new RuntimeError(`Υπάρχει ήδη υποπρόγραμμα με όνομα '${sub.name}'`, sub.line, sub.column);
      }
      if (BUILTIN_MATH_FUNCTION_NAMES.has(normalizeKeywordText(sub.name))) {
        throw new RuntimeError(`Υπάρχει ήδη έτοιμη μαθηματική συνάρτηση με όνομα '${sub.name}'`, sub.line, sub.column);
      }
      this.requireNoDuplicateParameters(sub);
      this.subprograms.set(sub.name, sub);
    }
  }

  /** Το ίδιο όνομα παραμέτρου δεν επιτρέπεται να εμφανίζεται δύο φορές στη λίστα παραμέτρων ενός υποπρογράμματος — αλλιώς το copy-in στο `callSubprogram` θα έσβηνε σιωπηλά την πρώτη τιμή με τη δεύτερη. */
  private requireNoDuplicateParameters(sub: Subprogram): void {
    const seen = new Set<string>();
    for (const param of sub.parameters) {
      if (seen.has(param)) {
        throw new RuntimeError(`Η παράμετρος '${param}' εμφανίζεται δύο φορές στο '${sub.name}'`, sub.line, sub.column);
      }
      seen.add(param);
    }
  }

  /**
   * "Τμήμα αλγορίθμου" — εκτελεί μια γυμνή λίστα εντολών (βλ.
   * `Parser.parseFragment`), χωρίς περίβλημα ΑΛΓΟΡΙΘΜΟΣ/ΠΡΟΓΡΑΜΜΑ, άρα
   * χωρίς δηλώσεις μεταβλητών ή έμμεση Δεδομένα/Αποτελέσματα είσοδο/έξοδο.
   * Μπορεί να συνοδεύεται από ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ δηλώσεις (`subprograms`,
   * βλ. `Parser.parseFragment`) — π.χ. ένα ΚΑΛΕΣΕ στις εντολές και η
   * αντίστοιχη ΔΙΑΔΙΚΑΣΙΑ δίπλα, χωρίς να χρειάζεται πλήρες ΠΡΟΓΡΑΜΜΑ.
   */
  async runStatements(
    statements: readonly Statement[],
    subprograms: readonly Subprogram[] = [],
    signal?: AbortSignal,
    onVariablesChanged?: VariablesListener,
    onStatementExecuting?: StatementListener,
    delayMs?: number,
    maxSteps?: number,
    onBreakpoint?: BreakpointHook,
  ): Promise<InterpretResult> {
    this.signal = signal;
    this.onVariablesChanged = onVariablesChanged;
    this.onStatementExecuting = onStatementExecuting;
    this.delayMs = delayMs ?? 0;
    this.maxSteps = maxSteps ?? DEFAULT_MAX_LOOP_STEPS;
    this.onBreakpoint = onBreakpoint;
    return this.runWithErrorHandling(async () => {
      this.registerSubprograms(subprograms);
      await this.executeStatements(statements);
    });
  }

  private async runWithErrorHandling(body: () => Promise<void>): Promise<InterpretResult> {
    try {
      await body();
      return { error: null, cancelled: false };
    } catch (e) {
      if (e instanceof RuntimeError) return { error: e, cancelled: false };
      if (e instanceof Cancelled) return { error: null, cancelled: true };
      throw e;
    }
  }

  /**
   * Το "Δεδομένα // Α, Β //" στην Ψευδογλώσσα ΔΕΝ είναι απλή τεκμηρίωση σε
   * πραγματικούς διερμηνευτές — λειτουργεί σαν έμμεσο ΔΙΑΒΑΣΕ: ζητά τιμή
   * για κάθε αναφερόμενη μεταβλητή πριν ξεκινήσει το σώμα του αλγορίθμου
   * (επιβεβαιωμένο από πραγματική δοκιμή σε πραγματικό διερμηνευτή, μαζί
   * με το ακριβές κείμενο μηνυμάτων). Η Ψευδογλώσσα δεν έχει τυπικές
   * δηλώσεις, άρα δεν ξέρουμε εκ των προτέρων αν ένα όνομα είναι απλή
   * μεταβλητή ή πίνακας — το καταλαβαίνουμε ψάχνοντας αν χρησιμοποιείται
   * ποτέ με δείκτη `[...]` μέσα στο σώμα (`detectArrayArity`).
   */
  private async promptForDataVariables(
    names: readonly string[],
    body: readonly Statement[],
    line: number,
    column: number,
  ): Promise<void> {
    if (names.length === 0) return;
    await this.io.output('Είσοδος δεδομένων:');
    for (const name of names) {
      const arity = detectArrayArity(name, body);
      if (arity === 0) {
        await this.promptScalarDataVariable(name);
      } else {
        await this.promptArrayDataVariable(name, arity, line, column);
      }
      this.onVariablesChanged?.(this.env.snapshot(), this.currentScope, null, null);
    }
  }

  private async promptScalarDataVariable(name: string): Promise<void> {
    await this.io.output(`Εισάγετε τιμή για την μεταβλητή «${name}» :`);
    const raw = await this.waitForInput();
    const value = this.parseInputValue(raw);
    this.env.define(name, value);
    if (!(value instanceof RuntimeArray)) {
      await this.io.output(`${name} = ${formatRuntimeValue(value)}`);
    }
  }

  /**
   * Πίνακας οποιουδήποτε πλήθους διαστάσεων: πρώτα ζητά ΟΛΕΣ τις διαστάσεις
   * μαζί σε ένα input χωρισμένο με κόμμα (π.χ. "5,3" για 5 γραμμές×3
   * στήλες), μετά ζητά κάθε στοιχείο ξεχωριστά με row-major σειρά (ο πρώτος
   * δείκτης αλλάζει πιο αργά) — επιβεβαιωμένο από πραγματικό διερμηνευτή
   * και για 1D και για 2D πίνακα.
   */
  private async promptArrayDataVariable(name: string, arity: number, line: number, column: number): Promise<void> {
    await this.io.output(`Δώσε Διαστάσεις πίνακα ${name} :`);
    const rawSize = await this.waitForInput();
    const dims = rawSize.split(',').map((part) => Number(part.trim()));
    if (dims.length !== arity || dims.some((d) => !Number.isInteger(d) || d <= 0)) {
      throw new RuntimeError(
        `Μη έγκυρες διαστάσεις πίνακα για '${name}' (αναμενόταν ${arity} θετικοί ακέραιοι χωρισμένοι με κόμμα): '${rawSize.trim()}'`,
        line,
        column,
      );
    }

    const array = new RuntimeArray(dims);
    this.env.define(name, array);

    const indices = new Array<number>(arity).fill(1);
    const fillDimension = async (dimIndex: number): Promise<void> => {
      if (dimIndex === arity) {
        await this.io.output(`${name}[${indices.join(', ')}] =`);
        const raw = await this.waitForInput();
        const value = this.parseInputValue(raw);
        this.requireConsistentArrayElementType(array, name, value, line, column);
        array.set(indices, value);
        return;
      }
      for (let i = 1; i <= dims[dimIndex]!; i++) {
        indices[dimIndex] = i;
        await fillDimension(dimIndex + 1);
      }
    };
    await fillDimension(0);
  }

  /**
   * Συμμετρικά, το "Αποτελέσματα // Μ //" εμφανίζει αυτόματα την τελική
   * τιμή κάθε αναφερόμενης μεταβλητής στο τέλος της εκτέλεσης, χωρίς να
   * χρειάζεται ρητό ΓΡΑΨΕ/Εμφάνισε (ίδια επιβεβαίωση με το Δεδομένα).
   */
  private async printResultVariables(names: readonly string[], line: number, column: number): Promise<void> {
    if (names.length === 0) return;
    await this.io.output('Έξοδος αποτελεσμάτων:');
    for (const name of names) {
      const value = this.lookupVariable(name, line, column);
      if (value instanceof RuntimeArray) {
        throw new RuntimeError(
          `Δεν μπορεί να εμφανιστεί ολόκληρος πίνακας ως αποτέλεσμα — '${name}' χρειάζεται δείκτη`,
          line,
          column,
        );
      }
      await this.io.output(`${name} = ${formatRuntimeValue(value)}`);
    }
  }

  private async checkCancellationAndYield(firstStatement?: Statement): Promise<void> {
    if (this.signal?.aborted) throw new Cancelled();
    this.stepCount++;
    if (this.stepCount > this.maxSteps) {
      throw new RuntimeError(
        `Πιθανός ατέρμων βρόχος: η εκτέλεση διακόπηκε αυτόματα μετά από ${this.maxSteps} βήματα`,
        firstStatement?.line ?? 1,
        firstStatement?.column ?? 1,
      );
    }
    if (this.stepCount % 1000 === 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      if (this.signal?.aborted) throw new Cancelled();
    }
  }

  /**
   * Καλείται μετά από ΚΑΘΕ εντολή (βλ. `executeStatements`) — ελέγχει αν
   * πέρασε αρκετός ΠΡΑΓΜΑΤΙΚΟΣ χρόνος (`UI_YIELD_INTERVAL_MS`) από την
   * τελευταία απόδοση ελέγχου, και αν ναι αποδίδει σε πραγματικό macrotask
   * (`setTimeout`) — ΟΧΙ απλά σε microtask, που δεν δίνει καμία ευκαιρία
   * στον browser να ζωγραφίσει (βλ. σχόλιο στο `UI_YIELD_INTERVAL_MS`).
   * Ξεχωριστό από το `checkCancellationAndYield` (που μετράει "βήματα"
   * εισόδου σε μπλοκ/βρόχο, όχι μεμονωμένες εντολές, και υπάρχει για να
   * εμποδίζει άτερμους βρόχους — διαφορετικός σκοπός). Πάρα πολύ φθηνό όταν
   * ΔΕΝ χρειάζεται απόδοση (μόνο ένα `Date.now()`), οπότε δεν επιβραδύνει
   * καθόλου προγράμματα που τελειώνουν γρήγορα.
   */
  private async maybeYieldToUI(): Promise<void> {
    const now = Date.now();
    if (now - this.lastUiYieldAt < UI_YIELD_INTERVAL_MS) return;
    this.lastUiYieldAt = now;
    if (this.signal?.aborted) throw new Cancelled();
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
    if (this.signal?.aborted) throw new Cancelled();
  }

  /**
   * Περιμένει είσοδο, αλλά «διακόπτεται» αμέσως αν ενεργοποιηθεί το signal —
   * σε αντίθεση με το `checkCancellationAndYield` (που ελέγχεται μόνο ΠΡΙΝ/
   * ΜΕΤΑ από κάθε εντολή), εδώ η εκτέλεση είναι ήδη «παγωμένη» περιμένοντας
   * το `io.input()` να επιστρέψει (π.χ. ο χρήστης δεν έχει ακόμα πατήσει
   * Enter σε ένα inline πεδίο ΔΙΑΒΑΣΕ) — χωρίς αυτό το race, το κουμπί
   * «Διακοπή» δεν θα έκανε τίποτα μέχρι να δοθεί πράγματι είσοδος.
   */
  private waitForInput(): Promise<string> {
    const inputPromise = this.io.input();
    const signal = this.signal;
    if (signal === undefined) return inputPromise;
    if (signal.aborted) return Promise.reject(new Cancelled());
    return new Promise<string>((resolve, reject) => {
      const onAbort = (): void => reject(new Cancelled());
      signal.addEventListener('abort', onAbort, { once: true });
      inputPromise.then(
        (value) => {
          signal.removeEventListener('abort', onAbort);
          resolve(value);
        },
        (err: unknown) => {
          signal.removeEventListener('abort', onAbort);
          reject(err instanceof Error ? err : new Error(String(err)));
        },
      );
    });
  }

  private reportVariables(): void {
    this.reportSequence++;
    const lastCondition = this.lastCondition;
    this.lastCondition = null;
    const lastArrayWrite = this.lastArrayWriteNames.size > 0 ? { arrayNames: [...this.lastArrayWriteNames] } : null;
    this.lastArrayWriteNames.clear();
    this.onVariablesChanged?.(this.env.snapshot(), this.currentScope, lastCondition, lastArrayWrite);
  }

  /** Καθυστέρηση "αργής εκτέλεσης", διακόπτεται αμέσως αν ενεργοποιηθεί το signal (ίδιο μοτίβο με `waitForInput`). */
  private waitDelay(): Promise<void> {
    if (this.delayMs <= 0) return Promise.resolve();
    const signal = this.signal;
    if (signal?.aborted) return Promise.reject(new Cancelled());
    return new Promise<void>((resolve, reject) => {
      const onAbort = (): void => {
        clearTimeout(timer);
        reject(new Cancelled());
      };
      const timer = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
      }, this.delayMs);
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  }

  /**
   * Breakpoint — βλ. `BreakpointHook`. Ο interpreter δεν ξέρει ποιες γραμμές
   * έχουν breakpoint· απλά καλεί το `onBreakpoint` και, αν αυτό επιστρέψει
   * `Promise`, περιμένει να λυθεί — ίδιο μοτίβο race-against-abort με το
   * `waitForInput` (ώστε το "Διακοπή" να δουλεύει ΚΑΙ ενώ ο αλγόριθμος είναι
   * παγωμένος σε breakpoint).
   */
  private waitForBreakpoint(line: number): Promise<void> {
    if (this.onBreakpoint === undefined) return Promise.resolve();
    const result = this.onBreakpoint(line);
    if (result === undefined) return Promise.resolve();
    const signal = this.signal;
    if (signal === undefined) return result;
    if (signal.aborted) return Promise.reject(new Cancelled());
    return new Promise<void>((resolve, reject) => {
      const onAbort = (): void => reject(new Cancelled());
      signal.addEventListener('abort', onAbort, { once: true });
      result.then(
        () => {
          signal.removeEventListener('abort', onAbort);
          resolve();
        },
        (err: unknown) => {
          signal.removeEventListener('abort', onAbort);
          reject(err instanceof Error ? err : new Error(String(err)));
        },
      );
    });
  }

  private async evaluateArrayDimension(expr: Expression): Promise<number> {
    const value = await this.evaluateNumericOperand(expr, 'τη διάσταση πίνακα');
    if (!Number.isInteger(value) || value <= 0) {
      throw new RuntimeError(
        `Η διάσταση πίνακα πρέπει να είναι θετικός ακέραιος, βρέθηκε ${value}`,
        expr.line,
        expr.column,
      );
    }
    return value;
  }

  // --- Εντολές -------------------------------------------------------------

  private async executeStatements(statements: readonly Statement[]): Promise<void> {
    await this.checkCancellationAndYield(statements[0]);
    for (const stmt of statements) {
      const sequenceBeforeStmt = this.reportSequence;
      await this.executeStatement(stmt);
      // Μια ΑΠΛΗ εντολή (ανάθεση/ΔΙΑΒΑΣΕ/ΓΡΑΨΕ/ΑΝΤΑΛΛΑΓΗ) δεν αναφέρει τίποτα
      // η ίδια — αυτή είναι η ΜΟΝΗ της αναφορά, πάντα χρειάζεται. Μια ΣΥΝΘΕΤΗ
      // εντολή (ΑΝ/ΓΙΑ/ΟΣΟ/ΜΕΧΡΙΣ_ΟΤΟΥ/ΕΠΙΛΕΞΕ/ΚΑΛΕΣΕ) συνήθως έχει ΗΔΗ
      // αναφέρει η ίδια, εσωτερικά, την τελική της κατάσταση (π.χ. η τελευταία
      // εντολή του κλάδου ΑΝ που ταίριαξε) — μια ΑΚΟΜΑ αναφορά εδώ θα ήταν
      // απλά διπλότυπη γραμμή με τις ΙΔΙΕΣ τιμές, χωρίς Συνθήκη/Έξοδο (bug
      // report: "γιατί το γράφει 2 φορές", αποκαλύφθηκε αφού αφαιρέθηκε η
      // "συμπαγής" προβολή Μεταβλητών που το έκρυβε σιωπηλά μέχρι τώρα).
      // Αναφέρουμε ΞΑΝΑ εδώ ΜΟΝΟ όταν πραγματικά χρειάζεται:
      //  - `reportSequence === sequenceBeforeStmt`: καμία εσωτερική αναφορά
      //    δεν έγινε καθόλου (απλή εντολή· ή ΑΝ/ΕΠΙΛΕΞΕ χωρίς κλάδο που
      //    ταίριαξε — βλ. `executeIf`/`executeSwitch`).
      //  - `this.lastCondition !== null`: έμεινε μια συνθήκη σε εκκρεμότητα,
      //    αναφερμένη σε ΑΛΛΗ (παλαιότερη) αναφορά — π.χ. το ΟΣΟ ΔΕΝ αναφέρει
      //    ρητά τον τελικό ψευδή έλεγχο πριν βγει από τον βρόχο (βλ.
      //    `executeWhile`), σε αντίθεση με ΓΙΑ/ΜΕΧΡΙΣ_ΟΤΟΥ που το κάνουν ήδη.
      if (this.reportSequence === sequenceBeforeStmt || this.lastCondition !== null) {
        this.reportVariables();
      }
      // Το breakpoint "παγώνει" ΜΕΤΑ την εντολή στη γραμμή του (όχι πριν) —
      // ο μαθητής θέλει να ΔΕΙ το αποτέλεσμά της (τιμές μεταβλητών ήδη
      // ενημερωμένες παραπάνω) πριν συνεχίσει, όχι να σταματήσει λίγο πριν.
      //
      // Το ΑΝ/ΕΠΙΛΕΞΕ εξαιρούνται εδώ: ελέγχουν ΗΔΗ breakpoint στη γραμμή
      // τους, ΑΜΕΣΩΣ μετά την αξιολόγηση συνθήκης/discriminant (βλ.
      // `executeIf`/`executeSwitch`) — η ΣΩΣΤΗ στιγμή, πριν μπουν στον
      // κλάδο/ΠΕΡΙΠΤΩΣΗ, όχι μετά από ΟΛΟΚΛΗΡΗ τη δομή (bug report: "βάζω
      // την τελίτσα στη συνθήκη... δεν σταματάει εκεί"). Αν ελέγχαμε ΞΑΝΑ
      // την ΙΔΙΑ γραμμή εδώ, θα πάγωνε ΔΕΥΤΕΡΗ φορά — ένα "κρυφό" δεύτερο
      // breakpoint hit που ο καλών δεν περιμένει να λύσει (bug: κρέμασε το
      // αντίστοιχο test, ο 2ος `resolve` δεν λυνόταν ΠΟΤΕ).
      if (stmt.kind !== 'IfStatement' && stmt.kind !== 'SwitchStatement') {
        await this.waitForBreakpoint(stmt.line);
      }
      await this.waitDelay();
      await this.maybeYieldToUI();
    }
  }

  private async executeStatement(stmt: Statement): Promise<void> {
    this.onStatementExecuting?.(stmt.line);
    switch (stmt.kind) {
      case 'AssignmentStatement':
        return this.executeAssignment(stmt);
      case 'ReadStatement':
        return this.executeRead(stmt);
      case 'WriteStatement':
        return this.executeWrite(stmt);
      case 'SwapStatement':
        return this.executeSwap(stmt);
      case 'IfStatement':
        return this.executeIf(stmt);
      case 'ForStatement':
        return this.executeFor(stmt);
      case 'WhileStatement':
        return this.executeWhile(stmt);
      case 'RepeatUntilStatement':
        return this.executeRepeatUntil(stmt);
      case 'SwitchStatement':
        return this.executeSwitch(stmt);
      case 'CallStatement':
        return this.executeCallStatement(stmt);
    }
  }

  /** ΔΙΑΒΑΣΕ/ΓΡΑΨΕ (και συνώνυμα) δεν επιτρέπονται μέσα σε ΣΥΝΑΡΤΗΣΗ — βλ. βιβλίο, §25.5 ερ.12/§25.6 "Παρατηρήσεις". */
  private forbidInsideFunction(verb: string, node: { readonly line: number; readonly column: number }): void {
    if (this.insideFunction) {
      throw new RuntimeError(`Το '${verb}' δεν επιτρέπεται μέσα σε ΣΥΝΑΡΤΗΣΗ`, node.line, node.column);
    }
  }

  // --- LValues (Identifier ή ArrayAccess) — ανάγνωση/εγγραφή ενοποιημένα ---

  private async readLValue(target: LValue): Promise<RuntimeValue> {
    if (target.kind === 'Identifier') {
      return this.lookupVariable(target.name, target.line, target.column);
    }
    return this.evaluateArrayAccess(target);
  }

  /** Μια ΣΤΑΘΕΡΑ (μόνο ΓΛΩΣΣΑ) δεν αλλάζει τιμή μετά την αρχική δήλωσή της — καμία νέα ανάθεση, ΔΙΑΒΑΣΕ, ΓΙΑ μετρητή, ή copy-out παραμέτρου. */
  private requireNotConstant(name: string, line: number, column: number): void {
    if (this.env.isConstant(name)) {
      throw new RuntimeError(`Η σταθερά '${name}' δεν μπορεί να αλλάξει τιμή`, line, column);
    }
  }

  /**
   * Δεν επιτρέπεται ανάθεση σε ΟΛΟΚΛΗΡΟ πίνακα (χωρίς δείκτη) — μόνο στα
   * μεμονωμένα στοιχεία του. Χωρίς αυτόν τον έλεγχο μια απλή τιμή θα
   * αντικαθιστούσε σιωπηλά ολόκληρο τον πίνακα (`env.define` δεν ξεχωρίζει
   * scalar από RuntimeArray), και το λάθος θα φαινόταν πολύ αργότερα, σε
   * κάποια άσχετη πρόσβαση `Α[i]` με ασαφές μήνυμα.
   */
  private requireNotWholeArrayAssignment(name: string, line: number, column: number): void {
    if (this.env.get(name) instanceof RuntimeArray) {
      throw new RuntimeError(`Δεν επιτρέπεται ανάθεση σε ολόκληρο πίνακα ('${name}') — μόνο στα μεμονωμένα στοιχεία του`, line, column);
    }
  }

  /**
   * Ο μετρητής ενός ΓΙΑ είναι "κλειδωμένος" όσο εκτελείται το σώμα του (βλ.
   * `executeFor`) — μόνο ο ίδιος ο βρόχος τον προχωράει, ο μαθητής δεν
   * επιτρέπεται να τον αλλάξει χειροκίνητα. Καλείται ΜΟΝΟ από τους
   * εκτελεστές εντολών (`executeAssignment`/`executeRead`/`executeSwap`) —
   * ΟΧΙ μέσα στο `assignToLValue` το ίδιο, ώστε να ΜΗΝ μπλοκάρει το
   * copy-out παραμέτρου μιας ΔΙΑΔΙΚΑΣΙΑΣ (π.χ. πέρασμα του μετρητή σαν
   * όρισμα μόνο για ανάγνωση — το copy-out ξαναγράφει ΠΑΝΤΑ την τιμή, ακόμα
   * κι αν δεν άλλαξε, οπότε δεν είναι το ίδιο πράγμα με ρητή ανάθεση).
   */
  private requireNotLockedForCounter(name: string, line: number, column: number): void {
    if (this.lockedForCounters.has(name)) {
      throw new RuntimeError(`Ο μετρητής '${name}' του ΓΙΑ δεν επιτρέπεται να αλλάξει τιμή μέσα στο σώμα του βρόχου`, line, column);
    }
  }

  private async assignToLValue(target: LValue, value: RuntimeValue): Promise<void> {
    if (target.kind === 'Identifier') {
      this.requireNotConstant(target.name, target.line, target.column);
      this.requireNotWholeArrayAssignment(target.name, target.line, target.column);
      this.checkDeclaredType(target.name, value, target.line, target.column);
      this.env.define(target.name, value);
      return;
    }
    const array = this.resolveArrayForWrite(target.array, target.indices);
    const indices = await this.evaluateIndices(target.indices);
    this.checkBounds(array, indices, target);
    this.checkDeclaredType(target.array.name, value, target.line, target.column);
    if (this.env.getDeclaredType(target.array.name) === undefined) {
      this.requireConsistentArrayElementType(array, target.array.name, value, target.line, target.column);
    }
    array.set(indices, value);
    this.lastArrayWriteNames.add(target.array.name);
  }

  /**
   * Η ΓΛΩΣΣΑ (σε αντίθεση με την Ψευδογλώσσα) απαιτεί ΚΑΘΕ μεταβλητή/πίνακα
   * να έχει δηλωθεί ρητά στο ΜΕΤΑΒΛΗΤΕΣ πριν χρησιμοποιηθεί — δεν υπάρχει
   * "σιωπηλή" δυναμική δημιουργία μεταβλητής όπως στην Ψευδογλώσσα. Καλείται
   * από `checkDeclaredType`, `resolveArrayForWrite` ΚΑΙ `executeFor` (ο
   * μετρητής ενός ΓΙΑ είναι ΚΙ αυτός απλή μεταβλητή που πρέπει να έχει
   * δηλωθεί) — τρία σημεία όπου διαφορετικά θα μπορούσε να «γεννηθεί»
   * σιωπηλά μια αδήλωτη μεταβλητή.
   */
  private requireDeclaredInGlossa(name: string, line: number, column: number): void {
    if (this.dialect === Dialect.GLOSSA && this.env.getDeclaredType(name) === undefined) {
      throw new RuntimeError(`Η μεταβλητή '${name}' δεν έχει δηλωθεί στο ΜΕΤΑΒΛΗΤΕΣ`, line, column);
    }
  }

  /**
   * Ελέγχει ότι μια τιμή ταιριάζει με τον δηλωμένο τύπο ενός ονόματος (αν
   * υπάρχει δήλωση — μόνο ΓΛΩΣΣΑ έχει τυπικές δηλώσεις, η Ψευδογλώσσα
   * παραμένει εντελώς δυναμική). Η ΑΚΕΡΑΙΑ είναι υποσύνολο της ΠΡΑΓΜΑΤΙΚΗΣ
   * (μια ΠΡΑΓΜΑΤΙΚΗ μεταβλητή δέχεται και ακέραιες τιμές, π.χ. `μ ← 0`) —
   * όλοι οι άλλοι συνδυασμοί απαιτούν ακριβή αντιστοιχία τύπου.
   */
  private checkDeclaredType(name: string, value: RuntimeValue, line: number, column: number): void {
    const declaredType = this.env.getDeclaredType(name);
    if (declaredType === undefined) {
      this.requireDeclaredInGlossa(name, line, column);
      return;
    }
    if (value instanceof RuntimeArray) return;
    if (this.valueMatchesDeclaredType(value, declaredType)) return;
    throw new RuntimeError(
      `Η μεταβλητή '${name}' είναι δηλωμένη ${declaredType} — δεν μπορεί να πάρει την τιμή ${describeRuntimeValue(value)}`,
      line,
      column,
    );
  }

  /**
   * Ένας πίνακας ΧΩΡΙΣ ρητή δήλωση τύπου (π.χ. σε "τμήμα αλγορίθμου", χωρίς
   * ΜΕΤΑΒΛΗΤΕΣ) "κλειδώνει" τον τύπο του από την ΠΡΩΤΗ τιμή που γράφεται σε
   * ΟΠΟΙΟΔΗΠΟΤΕ στοιχείο του — δεν επιτρέπεται ανάμειξη π.χ. ΑΚΕΡΑΙΩΝ και
   * ΠΡΑΓΜΑΤΙΚΩΝ (ή αριθμών/χαρακτήρων/λογικών) στον ίδιο πίνακα, ακριβώς
   * όπως ένας ρητά δηλωμένος πίνακας δέχεται μόνο έναν τύπο (βλ.
   * `checkDeclaredType`/`RuntimeArray.checkElementKind`). Καλείται ΜΟΝΟ όταν
   * ΔΕΝ υπάρχει ρητή δήλωση — αλλιώς το `checkDeclaredType` καλύπτει ήδη τον
   * έλεγχο τύπου.
   */
  private requireConsistentArrayElementType(array: RuntimeArray, name: string, value: RuntimeValue, line: number, column: number): void {
    if (value instanceof RuntimeArray) return;
    const existingType = array.checkElementKind(value);
    if (existingType !== null) {
      throw new RuntimeError(
        `Ο πίνακας '${name}' δέχεται μόνο έναν τύπο δεδομένων (${existingType}) — δεν μπορεί να πάρει την τιμή ${describeRuntimeValue(value)}`,
        line,
        column,
      );
    }
  }

  private valueMatchesDeclaredType(value: RuntimeValue, type: DeclaredType): boolean {
    switch (type) {
      case 'ΑΚΕΡΑΙΕΣ':
        return typeof value === 'number' && Number.isInteger(value);
      case 'ΠΡΑΓΜΑΤΙΚΕΣ':
        return typeof value === 'number';
      case 'ΧΑΡΑΚΤΗΡΕΣ':
        return typeof value === 'string';
      case 'ΛΟΓΙΚΕΣ':
        return typeof value === 'boolean';
    }
  }

  /** Δηλωμένος τύπος + αν είναι πίνακας (+ οι εκφράσεις διαστάσεων, για έλεγχο μεγέθους) για μια τυπική παράμετρο, από το ΜΕΤΑΒΛΗΤΕΣ του ΙΔΙΟΥ του υποπρογράμματος — `undefined` αν η παράμετρος δεν έχει (ρητά) δηλωθεί εκεί. */
  private findParameterDeclarationInfo(
    declarations: readonly Declaration[],
    paramName: string,
  ): { readonly declaredType: DeclaredType; readonly isArray: boolean; readonly dimensionExprs: readonly Expression[] } | undefined {
    for (const decl of declarations) {
      if (decl.kind !== 'VariableDeclaration') continue;
      for (const declarator of decl.declarators) {
        if (declarator.name === paramName) {
          return { declaredType: decl.declaredType, isArray: declarator.dimensions.length > 0, dimensionExprs: declarator.dimensions };
        }
      }
    }
    return undefined;
  }

  /**
   * Μια πραγματική παράμετρος πρέπει να ταιριάζει με τον δηλωμένο τύπο της
   * αντίστοιχης τυπικής (ίδιος κανόνας/μηχανισμός με `checkDeclaredType` για
   * κανονική ανάθεση) — αλλιώς το λάθος θα σκάσει αργότερα, μέσα στο σώμα
   * του υποπρογράμματος, με μπερδεμένο μήνυμα μακριά από την πραγματική αιτία.
   */
  private requireMatchingParameterType(
    paramName: string,
    declaredType: DeclaredType | undefined,
    value: RuntimeValue,
    line: number,
    column: number,
  ): void {
    if (declaredType === undefined || value instanceof RuntimeArray) return;
    if (this.valueMatchesDeclaredType(value, declaredType)) return;
    throw new RuntimeError(
      `Η παράμετρος '${paramName}' είναι δηλωμένη ${declaredType} — δεν μπορεί να πάρει την τιμή ${describeRuntimeValue(value)}`,
      line,
      column,
    );
  }

  /**
   * Μια πραγματική παράμετρος πρέπει να είναι πίνακας ΑΚΡΙΒΩΣ όταν η τυπική
   * είναι δηλωμένη πίνακας (και το αντίστροφο) — αλλιώς είτε σκάει
   * ασαφές σφάλμα μέσα στο σώμα (π.χ. "δεν είναι πίνακας" σε στοιχειώδη
   * τιμή), είτε — χειρότερα — το `registerDeclarations` του υποπρογράμματος
   * ΞΑΝΑΔΗΜΙΟΥΡΓΕΙ σιωπηλά έναν άδειο πίνακα πάνω από την ήδη αντιγραμμένη
   * τιμή, σβήνοντας τα δεδομένα που πέρασε ο καλών (βλ. `registerDeclarations`,
   * το `!this.env.has(declarator.name)` εκεί προϋποθέτει ότι αυτός ο έλεγχος
   * έχει ήδη επιβεβαιώσει ότι η τιμή είναι όντως πίνακας).
   */
  private requireMatchingParameterKind(
    paramName: string,
    info: { readonly declaredType: DeclaredType; readonly isArray: boolean } | undefined,
    value: RuntimeValue,
    line: number,
    column: number,
  ): void {
    if (info === undefined) return;
    const isArrayValue = value instanceof RuntimeArray;
    if (info.isArray && !isArrayValue) {
      throw new RuntimeError(`Η παράμετρος '${paramName}' είναι πίνακας — περίμενα πίνακα, όχι μεμονωμένη τιμή`, line, column);
    }
    if (!info.isArray && isArrayValue) {
      throw new RuntimeError(`Η παράμετρος '${paramName}' δεν είναι πίνακας — δεν μπορεί να πάρει πίνακα ως τιμή`, line, column);
    }
    if (!info.isArray) {
      this.requireMatchingParameterType(paramName, info.declaredType, value, line, column);
    }
  }

  /**
   * Όταν η πραγματική παράμετρος είναι πίνακας, οι διαστάσεις της πρέπει να
   * ταιριάζουν ΑΚΡΙΒΩΣ με τις δηλωμένες διαστάσεις της τυπικής — αλλιώς η
   * δήλωση μεγέθους μέσα στο υποπρόγραμμα γίνεται "διακοσμητική": αφού ο
   * πίνακας περνάει by-reference (καμία νέα δημιουργία, βλ.
   * `registerDeclarations`), ένας δείκτης εκτός του ΔΗΛΩΜΕΝΟΥ μεγέθους της
   * τυπικής θα δούλευε σιωπηλά αν έτυχε να χωράει στον ΠΡΑΓΜΑΤΙΚΟ πίνακα.
   * Καλείται με `this.env` ήδη = το τοπικό scope του υποπρογράμματος, ώστε
   * οι εκφράσεις διαστάσεων (π.χ. αναφορά σε ΣΤΑΘΕΡΑ) να λύνονται σωστά.
   */
  private async requireMatchingArraySize(
    paramName: string,
    dimensionExprs: readonly Expression[],
    value: RuntimeValue,
    line: number,
    column: number,
  ): Promise<void> {
    if (!(value instanceof RuntimeArray) || value.dimensions === null) return;
    const declaredDims: number[] = [];
    for (const expr of dimensionExprs) declaredDims.push(await this.evaluateArrayDimension(expr));
    const matches =
      declaredDims.length === value.dimensions.length && declaredDims.every((d, i) => d === value.dimensions![i]);
    if (!matches) {
      throw new RuntimeError(
        `Η παράμετρος '${paramName}' είναι δηλωμένη πίνακας [${declaredDims.join(', ')}] — δεν ταιριάζει με το μέγεθος του πίνακα που δόθηκε [${value.dimensions.join(', ')}]`,
        line,
        column,
      );
    }
  }

  private async evaluateArrayAccess(expr: ArrayAccess): Promise<RuntimeValue> {
    const array = this.resolveArrayForRead(expr.array);
    const indices = await this.evaluateIndices(expr.indices);
    this.checkBounds(array, indices, expr);
    const value = array.get(indices);
    if (value === undefined) {
      throw new RuntimeError(
        `Το στοιχείο '${expr.array.name}[${indices.join(', ')}]' δεν έχει αρχικοποιηθεί`,
        expr.line,
        expr.column,
      );
    }
    return value;
  }

  private async evaluateIndices(indexExprs: readonly Expression[]): Promise<number[]> {
    const indices: number[] = [];
    for (const idx of indexExprs) indices.push(await this.evaluateNumericOperand(idx, 'τον δείκτη πίνακα'));
    return indices;
  }

  private checkBounds(array: RuntimeArray, indices: readonly number[], node: Expression | ArrayAccess): void {
    if (!array.isWithinBounds(indices)) {
      throw new RuntimeError(`Ο δείκτης [${indices.join(', ')}] είναι εκτός ορίων του πίνακα`, node.line, node.column);
    }
  }

  /** Για ανάγνωση: η μεταβλητή πρέπει να υπάρχει ήδη και να είναι πίνακας. */
  private resolveArrayForRead(id: Identifier): RuntimeArray {
    const value = this.lookupVariable(id.name, id.line, id.column);
    if (!(value instanceof RuntimeArray)) {
      throw new RuntimeError(`Η μεταβλητή '${id.name}' δεν είναι πίνακας`, id.line, id.column);
    }
    return value;
  }

  /**
   * Για "αδήλωτο" πίνακα (ΓΛΩΣΣΑ "τμήμα αλγορίθμου", ή Ψευδογλώσσα χωρίς
   * "Δεδομένα") — αν ΚΑΘΕ δείκτης πρόσβασης είναι ΑΚΡΙΒΩΣ ο μετρητής ενός
   * ΓΙΑ που εκτελείται ΑΥΤΗ ΤΗ ΣΤΙΓΜΗ (βλ. `activeForLoopBounds`),
   * «μαντεύουμε» το μέγεθος του πίνακα από το ΑΝΩΤΑΤΟ όριο εκείνου του
   * βρόχου — π.χ. "ΓΙΑ ι ΑΠΟ 1 ΜΕΧΡΙ 10 ... Α[ι]" δίνει μέγεθος 10. Ο
   * πίνακας συμπεριφέρεται ΑΠΟ ΕΔΩ ΚΑΙ ΠΕΡΑ ακριβώς σαν να είχε δηλωθεί με
   * αυτό το μέγεθος — ίδιος έλεγχος ορίων (`checkBounds`/`isWithinBounds`,
   * απορρίπτει δείκτη εκτός ορίων σε ΕΠΟΜΕΝΗ πρόσβαση), ίδια πλήρης
   * εμφάνιση στο UI (βλ. `array.dimensions !== null` στο web/src/main.ts).
   * Αν έστω ΕΝΑΣ δείκτης δεν ταιριάζει με αυτό το μοτίβο (π.χ. σταθερός
   * αριθμός, ή έκφραση όπως "ι+1"), δεν μαντεύουμε τίποτα — "απόρριψη αντί
   * για μάντεμα" (ίδια φιλοσοφία με transform/subprogram.ts): ο πίνακας
   * παραμένει χωρίς όρια, στυλ Ψευδογλώσσας.
   */
  private inferArrayDimensionsFromLoopContext(indices: readonly Expression[]): number[] | null {
    const dims: number[] = [];
    for (const indexExpr of indices) {
      if (indexExpr.kind !== 'Identifier') return null;
      const bound = this.activeForLoopBounds.get(indexExpr.name);
      if (bound === undefined) return null;
      dims.push(bound);
    }
    return dims;
  }

  /**
   * Δεν επιτρέπεται η ΙΔΙΑ μεταβλητή-μετρητής σε εμφωλευμένους βρόχους
   * (ΓΙΑ/ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ, σε οποιονδήποτε συνδυασμό) — ο εσωτερικός θα
   * χαλούσε τον μετρητή του εξωτερικού. Ελέγχεται εδώ (δίχτυ ασφαλείας — το
   * κανονικό σημείο ελέγχου είναι το στατικό `findReusedLoopCounter`, πριν
   * καν ξεκινήσει η εκτέλεση) χρησιμοποιώντας το ΙΔΙΟ `activeForLoopBounds`
   * με το «μάντεμα ορίου πίνακα» — ένα όνομα εκεί μέσα σημαίνει ΗΔΗ κάποιος
   * ενεργός (εξωτερικός) βρόχος το χρησιμοποιεί ως μετρητή του.
   */
  private requireCounterNotReused(name: string, line: number, column: number): void {
    if (this.activeForLoopBounds.has(name)) {
      throw new RuntimeError(
        `Ο μετρητής '${name}' χρησιμοποιείται ήδη από εξωτερικό βρόχο — δεν επιτρέπεται η ίδια μεταβλητή-μετρητής σε εμφωλευμένους βρόχους`,
        line,
        column,
      );
    }
  }

  /**
   * Το ίδιο "μάντεμα ορίου πίνακα" (βλ. `inferArrayDimensionsFromLoopContext`)
   * πρέπει να δουλεύει ΚΑΙ όταν ο μαθητής γράφει τον βρόχο "με το χέρι" με
   * ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ αντί για ΓΙΑ (πολύ κοινό μοτίβο του βιβλίου, π.χ.
   * "ι←1 / ΟΣΟ ι<=10 ΕΠΑΝΑΛΑΒΕ / ... / ι←ι+1 / ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ") — δεν
   * υπάρχει εδώ ρητό "ΑΠΟ/ΜΕΧΡΙ" σαν στο ΓΙΑ, μόνο μια συνθήκη. Αναγνωρίζει
   * ΜΟΝΟ τα πιο κοινά, ξεκάθαρα μοτίβα ("ι<=Ν"/"ι<Ν" για ΟΣΟ, "ι>Ν"/"ι>=Ν"
   * για ΜΕΧΡΙΣ_ΟΤΟΥ — ο μετρητής ΠΑΝΤΑ αριστερά) — οτιδήποτε άλλο (π.χ.
   * αντίστροφη σειρά μελών, άλλος τελεστής, σύνθετη συνθήκη) δεν αναγνωρίζεται
   * ρητά ("απόρριψη αντί για μάντεμα"): επιστρέφει `null`, ο πίνακας μένει
   * χωρίς όρια όπως πριν — καμία αλλαγή συμπεριφοράς.
   */
  private async tryInferLoopBoundFromCondition(condition: Expression, kind: 'while' | 'until'): Promise<{ readonly name: string; readonly bound: number } | null> {
    if (condition.kind !== 'BinaryExpression' || condition.left.kind !== 'Identifier') return null;
    const name = condition.left.name;
    const evalBound = async (): Promise<number> => this.evaluateNumericOperand(condition.right, 'το όριο του βρόχου');
    if (kind === 'while') {
      if (condition.operator === '<=') return { name, bound: await evalBound() };
      if (condition.operator === '<') return { name, bound: (await evalBound()) - 1 };
      return null;
    }
    if (condition.operator === '>') return { name, bound: await evalBound() };
    if (condition.operator === '>=') return { name, bound: (await evalBound()) - 1 };
    return null;
  }

  /** Κοινό save/set/restore γύρω από ΟΣΟ/ΜΕΧΡΙΣ_ΟΤΟΥ — βλ. `tryInferLoopBoundFromCondition`. Ίδιο σκεπτικό με το save/restore μέσα στο `executeFor`. */
  private async withInferredLoopBound<T>(condition: Expression, kind: 'while' | 'until', body: () => Promise<T>): Promise<T> {
    const inferred = await this.tryInferLoopBoundFromCondition(condition, kind);
    if (inferred !== null) this.requireCounterNotReused(inferred.name, condition.line, condition.column);
    const previousBound = inferred !== null ? this.activeForLoopBounds.get(inferred.name) : undefined;
    if (inferred !== null) this.activeForLoopBounds.set(inferred.name, inferred.bound);
    try {
      return await body();
    } finally {
      if (inferred !== null) {
        if (previousBound === undefined) this.activeForLoopBounds.delete(inferred.name);
        else this.activeForLoopBounds.set(inferred.name, previousBound);
      }
    }
  }

  /**
   * Για εγγραφή: αν η μεταβλητή δεν υπάρχει ακόμα, δημιουργείται δυναμικά
   * (χωρίς προηγούμενη δήλωση) — ΕΚΤΟΣ ΓΛΩΣΣΑΣ πλήρους προγράμματος, όπου
   * αυτό είναι σφάλμα (βλ. `requireDeclaredInGlossa`· κανονικά πιάνεται
   * ΝΩΡΙΤΕΡΑ από το `findUndeclaredGlossaReference` πριν καν ξεκινήσει η
   * εκτέλεση — αυτός ο έλεγχος είναι δίχτυ ασφαλείας). Το μέγεθος
   * προσπαθεί πρώτα να «μαντευτεί» από τρέχοντα ΓΙΑ (βλ.
   * `inferArrayDimensionsFromLoopContext`) — αλλιώς μένει χωρίς όρια.
   * Αν υπάρχει ήδη ως πίνακας (π.χ. δηλωμένος στη ΓΛΩΣΣΑ), τον
   * ξαναχρησιμοποιεί — διατηρώντας τα όριά του.
   */
  private resolveArrayForWrite(id: Identifier, indices: readonly Expression[]): RuntimeArray {
    const existing = this.env.get(id.name);
    if (existing === undefined) {
      this.requireDeclaredInGlossa(id.name, id.line, id.column);
      const array = new RuntimeArray(this.inferArrayDimensionsFromLoopContext(indices));
      this.env.define(id.name, array);
      return array;
    }
    if (!(existing instanceof RuntimeArray)) {
      throw new RuntimeError(`Η μεταβλητή '${id.name}' δεν είναι πίνακας`, id.line, id.column);
    }
    return existing;
  }

  private async executeAssignment(stmt: AssignmentStatement): Promise<void> {
    if (stmt.target.kind === 'Identifier') {
      this.requireNotLockedForCounter(stmt.target.name, stmt.target.line, stmt.target.column);
    }
    const value = await this.evaluateExpression(stmt.value);
    await this.assignToLValue(stmt.target, value);
  }

  private async executeRead(stmt: ReadStatement): Promise<void> {
    this.forbidInsideFunction('ΔΙΑΒΑΣΕ', stmt);
    for (const target of stmt.targets) {
      if (target.kind === 'Identifier') {
        this.requireNotLockedForCounter(target.name, target.line, target.column);
      }
      const name = target.kind === 'Identifier' ? target.name : target.array.name;
      const declaredType = this.env.getDeclaredType(name);
      // Η ΔΙΑΒΑΣΕ δέχεται μόνο ΑΚΕΡΑΙΕΣ/ΠΡΑΓΜΑΤΙΚΕΣ/ΧΑΡΑΚΤΗΡΕΣ (σύμβαση
      // βιβλίου) — όχι ΛΟΓΙΚΕΣ, δεν υπάρχει τυπικός τρόπος να "πληκτρολογηθεί"
      // μια λογική τιμή. Πριν καν περιμένουμε είσοδο, ώστε να μην ζητάμε
      // δεδομένα που θα απορριφθούν σίγουρα.
      if (declaredType === 'ΛΟΓΙΚΕΣ') {
        throw new RuntimeError(`Η ΔΙΑΒΑΣΕ δεν δέχεται μεταβλητές τύπου ΛΟΓΙΚΕΣ ('${name}')`, target.line, target.column);
      }
      const raw = await this.waitForInput();
      await this.assignToLValue(target, this.parseInputValue(raw, declaredType));
      // Αναφορά ΑΜΕΣΩΣ μετά από ΚΑΘΕ στόχο (όχι μία φορά, στο τέλος όλου του
      // ΔΙΑΒΑΣΕ) — σε "ΔΙΑΒΑΣΕ Α[i], Β[i]" (πάνω από έναν στόχο στην ΙΔΙΑ
      // εντολή, βλ. `executeStatements`/`reportSequence`), χωρίς αυτό ο
      // πίνακας Α έμενε «κρυφός» μέχρι να δοθεί ΚΑΙ η τιμή του Β — bug
      // report: "γιατί δεν μου το εμφανίζει... και πρέπει να βάλω και το
      // άλλο για να μου το εμφανίσει ταυτόχρονα". Αβλαβές (απλά μετακινεί
      // την ΜΟΝΑΔΙΚΗ αναφορά νωρίτερα, εσωτερικά) για το σύνηθες ΔΙΑΒΑΣΕ ΕΝΟΣ
      // στόχου — το `executeStatements` πλέον απλά παραλείπει τη δική του,
      // πια περιττή, αναφορά στο τέλος.
      this.reportVariables();
    }
  }

  /**
   * Αναγνωρίζει αριθμό/ΑΛΗΘΗΣ/ΨΕΥΔΗΣ από το ακατέργαστο κείμενο εισόδου·
   * αλλιώς το κρατά ως συμβολοσειρά. Αν ο στόχος έχει δηλωμένο τύπο ΓΛΩΣΣΑΣ,
   * τον χρησιμοποιούμε ως υπόδειξη αντί να μαντεύουμε από το σχήμα του
   * κειμένου — π.χ. ΔΙΑΒΑΣΕ σε ΧΑΡΑΚΤΗΡΕΣ μεταβλητή πρέπει να κρατά το
   * "5" ως χαρακτήρας-συμβολοσειρά, όχι να το μετατρέπει σε αριθμό.
   */
  private parseInputValue(raw: string, declaredType?: DeclaredType): RuntimeValue {
    if (declaredType === 'ΧΑΡΑΚΤΗΡΕΣ') return raw;
    const trimmed = raw.trim();
    if (declaredType === 'ΛΟΓΙΚΕΣ') {
      const normalized = normalizeKeywordText(trimmed);
      if (normalized === 'ΑΛΗΘΗΣ') return true;
      if (normalized === 'ΨΕΥΔΗΣ') return false;
      return raw; // θα απορριφθεί από τον έλεγχο τύπου με σαφές μήνυμα
    }
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
    const normalized = normalizeKeywordText(trimmed);
    if (normalized === 'ΑΛΗΘΗΣ') return true;
    if (normalized === 'ΨΕΥΔΗΣ') return false;
    return raw;
  }

  private async executeWrite(stmt: WriteStatement): Promise<void> {
    this.forbidInsideFunction(stmt.verb ?? 'ΓΡΑΨΕ', stmt);
    const parts: string[] = [];
    for (const v of stmt.values) {
      const value = await this.evaluateExpression(v);
      if (value instanceof RuntimeArray) {
        throw new RuntimeError(
          'Δεν μπορεί να τυπωθεί ολόκληρος πίνακας — χρειάζεται δείκτης (π.χ. Α[i])',
          v.line,
          v.column,
        );
      }
      parts.push(formatRuntimeValue(value));
    }
    await this.io.output(parts.join(' '));
  }

  private async executeSwap(stmt: SwapStatement): Promise<void> {
    if (stmt.left.kind === 'Identifier') {
      this.requireNotLockedForCounter(stmt.left.name, stmt.left.line, stmt.left.column);
    }
    if (stmt.right.kind === 'Identifier') {
      this.requireNotLockedForCounter(stmt.right.name, stmt.right.line, stmt.right.column);
    }
    const leftValue = await this.readLValue(stmt.left);
    const rightValue = await this.readLValue(stmt.right);
    await this.assignToLValue(stmt.left, rightValue);
    await this.assignToLValue(stmt.right, leftValue);
  }

  private async executeIf(stmt: IfStatement): Promise<void> {
    let matchedBody: readonly Statement[] | null = null;
    // Βλ. `VariablesListener`/`lastCondition` (στήλη "Συνθήκη" στο web UI) —
    // ΙΔΙΟ σκεπτικό με ΓΙΑ/ΟΣΟ/ΜΕΧΡΙΣ_ΟΤΟΥ, τώρα ΚΑΙ για δομές επιλογής
    // (ζητήθηκε ρητά: "θέλω να φαίνονται όλες οι συνθήκες... και για τις
    // δομές επιλογής"). Το αποτέλεσμα καταναλώνεται από την ΕΠΟΜΕΝΗ αναφορά
    // μεταβλητών — είτε από την πρώτη εντολή του κλάδου που ταίριαξε (αν
    // αληθές) είτε από τον καλούντα μόλις επιστρέψουμε (αν ψευδές, χωρίς
    // άλλον κλάδο να ταιριάξει).
    const ifConditionText = printExpression(stmt.condition, this.dialect);
    const ifResult = await this.evaluateBooleanCondition(stmt.condition, 'τη συνθήκη του ΑΝ');
    this.lastCondition = { result: ifResult, text: ifConditionText };
    // Παύση στη γραμμή του ΑΝ ΑΦΟΥ αξιολογηθεί η συνθήκη — χωρίς αυτό, το
    // highlight της γραμμής ΑΝ (που έχει ήδη μπει από το `executeStatement`,
    // τον γενικό dispatcher, βλ. εκεί) αντικαθίστατο ΑΜΕΣΩΣ από την πρώτη
    // εντολή του κλάδου που ταίριαξε πριν προλάβει ο browser να το
    // ζωγραφίσει καν — σε αντίθεση με ΓΙΑ/ΟΣΟ/ΜΕΧΡΙΣ_ΟΤΟΥ, το ΑΝ ΔΕΝ
    // επαναλαμβάνεται, οπότε δεν υπάρχει "2η φορά" να αντισταθμίσει το
    // χαμένο πρώτο highlight (bug report: "η γραμμή που δείχνει ποια
    // γραμμή εκτελείται δεν περνάει από τη συνθήκη" — μαζί με το αντίστοιχο
    // "δεν περνάει από τον ρόμβο" στο διάγραμμα ροής, αφού και τα δύο
    // τροφοδοτούνται από το ΙΔΙΟ `onStatementExecuting`, βλ. `main.ts`:
    // `highlightCurrentLine`). Ίδια τεχνική/σκεπτικό με ΓΙΑ/ΟΣΟ/ΜΕΧΡΙΣ_ΟΤΟΥ
    // (`waitDelay` είναι no-op εκτός "Αργής εκτέλεσης", άρα μηδενικό κόστος
    // όταν είναι σβηστή).
    //
    // Το `waitForBreakpoint(stmt.line)` εδώ είναι ΞΕΧΩΡΙΣΤΟ bug report: ένα
    // breakpoint («τελίτσα») πάνω στη γραμμή ΑΝ δεν σταματούσε ΠΟΤΕ εκεί —
    // ο ΜΟΝΑΔΙΚΟΣ έλεγχος breakpoint για τη γραμμή ΑΝ γινόταν στο γενικό
    // `executeStatements` (βλ. εκεί), ΑΦΟΥ ΕΙΧΕ ΗΔΗ τρέξει ΟΛΟΚΛΗΡΟ το
    // ταιριασμένο σώμα — δηλαδή το breakpoint "έσπαγε" μετά την ΤΕΛΕΙΩΜΕΝΗ
    // δομή ΑΝ, όχι στην ίδια τη συνθήκη όπως θα περίμενε ο μαθητής. Ρητός
    // έλεγχος εδώ, ΑΜΕΣΩΣ μετά την αξιολόγηση, ώστε να σταματάει ΠΡΑΓΜΑΤΙΚΑ
    // εκεί, πριν μπει στον κλάδο.
    await this.waitForBreakpoint(stmt.line);
    await this.waitDelay();
    if (ifResult) {
      matchedBody = stmt.thenBranch;
    } else {
      for (const branch of stmt.elseIfBranches) {
        // Η προηγούμενη (ψευδής) συνθήκη πρέπει να αναφερθεί ΤΩΡΑ, πριν την
        // αντικαταστήσουμε ελέγχοντας την ΕΠΟΜΕΝΗ — σε αντίθεση με τους
        // βρόχους, ΚΑΜΙΑ εντολή σώματος δεν εκτελείται ανάμεσα σε δύο
        // ελέγχους ΑΛΛΙΩΣ_ΑΝ για να την "καταναλώσει" φυσικά, οπότε θα
        // χανόταν σιωπηλά χωρίς αυτό το ρητό reportVariables εδώ.
        this.reportVariables();
        // Καμία προηγούμενη κλήση δεν είχε δείξει ΠΟΤΕ τη γραμμή ΑΛΛΙΩΣ_ΑΝ
        // (σε αντίθεση με το ΑΝ, που τουλάχιστον παίρνει την αρχική
        // υπογράμμιση από τον dispatcher) — ρητό highlight+παύση εδώ, ίδιος
        // λόγος με παραπάνω. Η γραμμή της συνθήκης (`branch.condition.line`)
        // ταυτίζεται με τη γραμμή του ΑΛΛΙΩΣ_ΑΝ, ίδια τεχνική με το ΜΕΧΡΙΣ_ΟΤΟΥ.
        this.onStatementExecuting?.(branch.condition.line);
        await this.waitDelay();
        const branchText = printExpression(branch.condition, this.dialect);
        const branchResult = await this.evaluateBooleanCondition(branch.condition, 'τη συνθήκη του ΑΛΛΙΩΣ_ΑΝ');
        this.lastCondition = { result: branchResult, text: branchText };
        // Ίδιο bug report με το ΑΝ παραπάνω — breakpoint πάνω σε ΑΛΛΙΩΣ_ΑΝ
        // δεν σταματούσε ΠΟΤΕ εκεί (ούτε καν "αργά", αφού δεν υπήρχε ΚΑΝΕΝΑΣ
        // έλεγχος breakpoint γι' αυτή τη γραμμή, πουθενά).
        await this.waitForBreakpoint(branch.condition.line);
        if (branchResult) {
          matchedBody = branch.body;
          break;
        }
      }
      if (matchedBody === null) matchedBody = stmt.elseBranch;
    }
    if (matchedBody !== null) await this.executeStatements(matchedBody);
    // Δείχνει ΚΑΙ το ΤΕΛΟΣ_ΑΝ όταν η εκτέλεση φτάνει εκεί — ΚΑΘΕ κλάδος
    // (ή και κανένας, αν η συνθήκη ήταν ψευδής χωρίς ΑΛΛΙΩΣ) καταλήγει εδώ
    // πριν συνεχίσει στην επόμενη εντολή· `null` για τη μονογραμμική μορφή
    // Ψευδογλώσσας, που δεν έχει καν ΤΕΛΟΣ_ΑΝ (βλ. `IfStatement.endLine`).
    if (stmt.endLine !== null) {
      this.onStatementExecuting?.(stmt.endLine);
      await this.waitDelay();
    }
  }

  /**
   * ΕΠΙΛΕΞΕ ... ΠΕΡΙΠΤΩΣΗ τιμή1, τιμή2 ... [ΠΕΡΙΠΤΩΣΗ ΑΛΛΙΩΣ ...] ΤΕΛΟΣ_ΕΠΙΛΟΓΩΝ.
   * Σαν το ΑΝ/ΑΛΛΙΩΣ_ΑΝ — εκτελεί ΜΟΝΟ το σώμα της πρώτης ΠΕΡΙΠΤΩΣΗΣ όπου η
   * τιμή του discriminant ταιριάζει με μία από τις τιμές της (χωρίς
   * "fall-through" σε επόμενες περιπτώσεις, σε αντίθεση με C-style switch).
   */
  private async executeSwitch(stmt: SwitchStatement): Promise<void> {
    const discriminant = await this.evaluateExpression(stmt.discriminant);
    if (discriminant instanceof RuntimeArray) {
      throw new RuntimeError(
        'Δεν μπορεί να χρησιμοποιηθεί ολόκληρος πίνακας ως έκφραση στο ΕΠΙΛΕΞΕ — χρειάζεται δείκτης (π.χ. Α[i])',
        stmt.discriminant.line,
        stmt.discriminant.column,
      );
    }
    // Παύση στη γραμμή του ΕΠΙΛΕΞΕ ΑΦΟΥ υπολογιστεί ο discriminant — ΙΔΙΟ
    // bug/σκεπτικό με το ΑΝ (βλ. `executeIf`): χωρίς αυτό, το highlight (και
    // το αντίστοιχο βήμα στο διάγραμμα ροής) πηδούσε κατευθείαν στην
    // ΠΕΡΙΠΤΩΣΗ που ταίριαξε, χωρίς ΠΟΤΕ να φανεί η ίδια η γραμμή ΕΠΙΛΕΞΕ.
    // Το `waitForBreakpoint` εδώ είναι το αντίστοιχο του δεύτερου bug report
    // (breakpoint που «δεν σταματάει εκεί») — βλ. εξαίρεση στο
    // `executeStatements` πιο κάτω στο αρχείο, ίδιος λόγος με το ΑΝ.
    await this.waitForBreakpoint(stmt.line);
    await this.waitDelay();
    let matchedBody: readonly Statement[] | null = null;
    for (const switchCase of stmt.cases) {
      // Καμία ΠΕΡΙΠΤΩΣΗ δεν υπογραμμιζόταν ΠΟΤΕ πριν — ρητό highlight+παύση
      // εδώ για ΚΑΘΕ ΠΕΡΙΠΤΩΣΗ που ελέγχεται, ίδια τεχνική με το ΑΛΛΙΩΣ_ΑΝ.
      // Η ΠΕΡΙΠΤΩΣΗ δεν έχει δική της γραμμή στο AST (`SwitchCase` δεν
      // επεκτείνει `NodeBase`) — η πρώτη τιμή της λίστας (`values[0]`) είναι
      // πάντα στην ΙΔΙΑ γραμμή με τη λέξη ΠΕΡΙΠΤΩΣΗ, οπότε τη δανειζόμαστε.
      const caseLine = switchCase.values[0]?.line ?? stmt.line;
      this.onStatementExecuting?.(caseLine);
      await this.waitDelay();
      let caseMatched = false;
      for (const value of switchCase.values) {
        if (await this.matchesSwitchCaseValue(discriminant, value)) {
          matchedBody = switchCase.body;
          caseMatched = true;
          break;
        }
      }
      await this.waitForBreakpoint(caseLine);
      if (caseMatched) break;
    }
    if (matchedBody === null) matchedBody = stmt.elseBody;
    if (matchedBody !== null) await this.executeStatements(matchedBody);
    // Δείχνει ΚΑΙ το ΤΕΛΟΣ_ΕΠΙΛΟΓΩΝ όταν η εκτέλεση φτάνει εκεί — ίδιο
    // σκεπτικό με το ΤΕΛΟΣ_ΑΝ (βλ. `executeIf`).
    if (stmt.endLine !== null) {
      this.onStatementExecuting?.(stmt.endLine);
      await this.waitDelay();
    }
  }

  /**
   * Ταίριασμα μιας τιμής ΠΕΡΙΠΤΩΣΗΣ με το (ήδη υπολογισμένο, μία φορά) 'discriminant':
   * - Διάστημα "10..15": 10 <= discriminant <= 15 (αν from > to, ποτέ δεν ταιριάζει — δεν είναι σφάλμα).
   * - Συγκριτικός τελεστής "> 20": συγκρίνει το discriminant με την τιμή.
   * - Απλή τιμή: ακριβές ταίριασμα (===), όπως πριν.
   */
  private async matchesSwitchCaseValue(discriminant: RuntimeValue, caseValue: SwitchCaseValue): Promise<boolean> {
    if (caseValue.kind === 'CaseRange') {
      const from = await this.evaluateExpression(caseValue.from);
      const to = await this.evaluateExpression(caseValue.to);
      return (
        this.compareOrderedValues(discriminant, from, caseValue.line, caseValue.column) >= 0 &&
        this.compareOrderedValues(discriminant, to, caseValue.line, caseValue.column) <= 0
      );
    }
    if (caseValue.kind === 'CaseComparison') {
      const value = await this.evaluateExpression(caseValue.value);
      if (caseValue.operator === '<>') return discriminant !== value;
      const cmp = this.compareOrderedValues(discriminant, value, caseValue.line, caseValue.column);
      switch (caseValue.operator) {
        case '>':
          return cmp > 0;
        case '<':
          return cmp < 0;
        case '>=':
          return cmp >= 0;
        case '<=':
          return cmp <= 0;
      }
    }
    return (await this.evaluateExpression(caseValue)) === discriminant;
  }

  /** Σύγκριση διάταξης για διάστημα/συγκριτικό τελεστή σε ΠΕΡΙΠΤΩΣΗ — δουλεύει σε αριθμούς ή σε συμβολοσειρές (όχι μεικτά, όχι λογικές τιμές/πίνακες). */
  private compareOrderedValues(a: RuntimeValue, b: RuntimeValue, line: number, column: number): number {
    if (typeof a === 'number' && typeof b === 'number') return a < b ? -1 : a > b ? 1 : 0;
    if (typeof a === 'string' && typeof b === 'string') return a < b ? -1 : a > b ? 1 : 0;
    throw new RuntimeError(
      `Δεν είναι δυνατή η σύγκριση ${describeRuntimeValue(a)} με ${describeRuntimeValue(b)} σε ΠΕΡΙΠΤΩΣΗ με διάστημα ή συγκριτικό τελεστή`,
      line,
      column,
    );
  }

  private async executeFor(stmt: ForStatement): Promise<void> {
    const from = await this.evaluateNumericOperand(stmt.from, "το 'ΑΠΟ' του ΓΙΑ");
    const to = await this.evaluateNumericOperand(stmt.to, "το 'ΜΕΧΡΙ' του ΓΙΑ");
    const step = stmt.step === null ? 1 : await this.evaluateNumericOperand(stmt.step, "το 'ΜΕ_ΒΗΜΑ' του ΓΙΑ");

    if (step === 0) {
      throw new RuntimeError('Το βήμα του ΓΙΑ δεν μπορεί να είναι 0', stmt.line, stmt.column);
    }
    // Ο μετρητής του ΓΙΑ είναι ΚΙ αυτός απλή μεταβλητή — στη ΓΛΩΣΣΑ πρέπει
    // να έχει δηλωθεί ΚΙ αυτός (βλ. `requireDeclaredInGlossa`· δίχτυ
    // ασφαλείας, το κανονικό σημείο ελέγχου είναι `findUndeclaredGlossaReference`).
    this.requireDeclaredInGlossa(stmt.variable.name, stmt.variable.line, stmt.variable.column);
    this.requireNotConstant(stmt.variable.name, stmt.variable.line, stmt.variable.column);
    this.requireCounterNotReused(stmt.variable.name, stmt.variable.line, stmt.variable.column);

    // Καταγράφει το ανώτατο όριο αυτού του ΓΙΑ όσο είναι ενεργό — βλ.
    // `inferArrayDimensionsFromLoopContext`. Αποκαθιστά το προηγούμενο (αν
    // υπήρχε, π.χ. εξωτερικός βρόχος με το ΙΔΙΟ όνομα μετρητή) στο τέλος.
    const previousBound = this.activeForLoopBounds.get(stmt.variable.name);
    this.activeForLoopBounds.set(stmt.variable.name, Math.max(from, to));
    // Κλειδώνει τον μετρητή όσο τρέχει το σώμα — βλ. `requireNotLockedForCounter`.
    // Το `requireCounterNotReused` παραπάνω εγγυάται ότι δεν είναι ΗΔΗ
    // κλειδωμένος (καμία εμφωλευμένη επαναχρησιμοποίηση του ίδιου ονόματος).
    this.lockedForCounters.add(stmt.variable.name);
    // Η ΠΡΩΤΗ φορά υπογραμμίζεται ήδη από το `executeStatement` (γενικός
    // dispatcher, βλ. εκεί) — από τη 2η επανάληψη και μετά ξαναδείχνουμε τη
    // γραμμή του ΓΙΑ εμείς, ώστε να φαίνεται ότι ο βρόχος ΓΥΡΝΑΕΙ πίσω στο
    // ΓΙΑ για τον επόμενο μετρητή (ίδιος λόγος με το ΟΣΟ, βλ. `executeWhile`)
    // — αλλιώς, με σώμα ΜΙΑΣ εντολής (π.χ. μόνο ΔΙΑΒΑΣΕ μέσα σε εμφωλευμένα
    // ΓΙΑ γεμίζοντας πίνακα), η υπογράμμιση έμενε "κολλημένη" στην ίδια
    // εσωτερική γραμμή σε όλη τη διάρκεια (bug report).
    // Το ισοδύναμο κείμενο συνθήκης του μοντέλου ΟΣΟ (βλ. σχόλιο πιο κάτω) —
    // ίδιο σε ΚΑΘΕ επανάληψη, υπολογίζεται μία φορά. Βλ. `VariablesListener`
    // (στήλη "Συνθήκη" στο web UI) — δείχνει ΠΟΙΑ ακριβώς συνθήκη ελέγχθηκε,
    // κρίσιμο σε εμφωλευμένα ΓΙΑ όπου μια γυμνή Αληθής/Ψευδής δεν αρκεί.
    const conditionText = `${stmt.variable.name} ${step > 0 ? '<=' : '>='} ${printExpression(stmt.to, this.dialect)}`;
    let isFirstIteration = true;
    let i = from;
    try {
      for (; step > 0 ? i <= to : i >= to; i += step) {
        // Ο μετρητής ενημερώνεται εδώ με `env.define` απευθείας (όχι μέσω
        // `assignToLValue`), άρα πρέπει να επαναλάβουμε ρητά τον ίδιο έλεγχο
        // δηλωμένου τύπου που ισχύει σε ΚΑΘΕ άλλη ανάθεση — αλλιώς μια
        // ΑΚΕΡΑΙΑ μεταβλητή θα μπορούσε σιωπηλά να πάρει δεκαδική τιμή ως
        // μετρητής ΓΙΑ (π.χ. ΑΠΟ 1.5 ή ΜΕ_ΒΗΜΑ 0.5).
        this.checkDeclaredType(stmt.variable.name, i, stmt.variable.line, stmt.variable.column);
        this.env.define(stmt.variable.name, i);
        // Ο (σιωπηλός) έλεγχος "i <= τέλος"/"i >= τέλος" μόλις πέτυχε — βλ.
        // `VariablesListener`/`lastCondition` (στήλη "Συνθήκη" στο web UI).
        this.lastCondition = { result: true, text: conditionText };
        this.reportVariables();
        if (isFirstIteration) isFirstIteration = false;
        else {
          this.onStatementExecuting?.(stmt.line);
          // Χωρίς αυτό, το highlight της γραμμής ΓΙΑ προλαβαίνει να
          // αντικατασταθεί αμέσως από την πρώτη εντολή του σώματος πριν
          // προλάβει ο browser να το ζωγραφίσει καν (βλ. `waitDelay` — no-op
          // εκτός "Αργής εκτέλεσης", άρα μηδενικό κόστος όταν είναι σβηστή).
          await this.waitDelay();
        }
        await this.executeStatements(stmt.body);
        // Ξαναδείχνει ΚΑΙ το ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ σε ΚΑΘΕ επανάληψη — αντιστοιχεί
        // στο ορθογώνιο αύξησης "i ← i + βήμα" του διαγράμματος ροής (βλ.
        // `flowchart.ts` renderForLoop), που το ΓΙΑ δεν έχει καν δική του
        // γραμμή πηγαίου κώδικα για αυτό. Σειρά ανά επανάληψη: ΓΙΑ (έλεγχος)
        // → σώμα (μία-μία εντολή) → ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ (αύξηση) → πάλι ΓΙΑ.
        if (stmt.endLine !== null) {
          this.onStatementExecuting?.(stmt.endLine);
          await this.waitDelay();
        }
      }
      // Το ΓΙΑ είναι, σύμφωνα με το βιβλίο, "συντακτική ζάχαρη" πάνω από ΟΣΟ
      // με ρητή αύξηση ΜΕΣΑ στο σώμα — "μ ← αρχή· ΟΣΟ μ<=τέλος ΕΠΑΝΑΛΑΒΕ
      // <σώμα>· μ ← μ+βήμα· ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ" (βλ. `transform/repetition.ts`
      // forToWhile, `printer/natural-language.ts` §14.1, `flowchart.ts`
      // renderForLoop — όλα ήδη μοντελοποιούν το ΓΙΑ ΕΤΣΙ). Επομένως ο
      // μετρητής ΔΕΝ μένει στην τελευταία έγκυρη τιμή του σώματος — φτάνει
      // στην τιμή που ΑΠΕΤΥΧΕ τον τελικό έλεγχο (π.χ. "ΓΙΑ ι ΑΠΟ 1 ΜΕΧΡΙ 10"
      // αφήνει ι=11, όχι 10 — bug report, επιβεβαιωμένο ότι ταιριάζει με το
      // επίσημο βιβλίο ΑΕΠΠ). Σε βρόχο που ΔΕΝ έτρεξε καθόλου (π.χ. "ΑΠΟ 5
      // ΜΕΧΡΙ 1"), ο μετρητής μένει στην αρχική του τιμή (i === from εδώ,
      // αφού ποτέ δεν αυξήθηκε) — ίδια συμπεριφορά με το αντίστοιχο ΟΣΟ.
      this.checkDeclaredType(stmt.variable.name, i, stmt.variable.line, stmt.variable.column);
      this.env.define(stmt.variable.name, i);
      // Ο τελικός έλεγχος μόλις απέτυχε — βλ. σχόλιο παραπάνω.
      this.lastCondition = { result: false, text: conditionText };
      this.reportVariables();
      if (!isFirstIteration) {
        this.onStatementExecuting?.(stmt.line);
        await this.waitDelay();
      }
    } finally {
      this.lockedForCounters.delete(stmt.variable.name);
      if (previousBound === undefined) this.activeForLoopBounds.delete(stmt.variable.name);
      else this.activeForLoopBounds.set(stmt.variable.name, previousBound);
    }
  }

  private async executeWhile(stmt: WhileStatement): Promise<void> {
    await this.withInferredLoopBound(stmt.condition, 'while', async () => {
      // Η ΠΡΩΤΗ φορά υπογραμμίζεται ήδη από το `executeStatement` (γενικός
      // dispatcher, βλ. εκεί) — εδώ ξαναδείχνουμε τη γραμμή του ΟΣΟ ΜΕΤΑ από
      // κάθε επανάληψη του σώματος, ώστε να φαίνεται ότι ο βρόχος ΓΥΡΝΑΕΙ
      // πίσω στο ΟΣΟ για τον επόμενο έλεγχο (ίδιος λόγος με το ΓΙΑ, βλ.
      // `executeFor`) — όχι μόνο ότι επαναλαμβάνεται η ίδια εσωτερική γραμμή.
      const conditionText = printExpression(stmt.condition, this.dialect);
      for (;;) {
        const conditionResult = await this.evaluateBooleanCondition(stmt.condition, 'τη συνθήκη του ΟΣΟ');
        // Βλ. `VariablesListener`/`lastCondition` (στήλη "Συνθήκη" στο
        // web UI) — καταναλώνεται από την ΕΠΟΜΕΝΗ αναφορά μεταβλητών: είτε
        // από την πρώτη εντολή του σώματος (αν αληθές) είτε από τον
        // καλούντα, αμέσως μετά την επιστροφή από εδώ (αν ψευδές, βλ. `break`).
        this.lastCondition = { result: conditionResult, text: conditionText };
        if (!conditionResult) break;
        await this.executeStatements(stmt.body);
        // Ξαναδείχνει ΚΑΙ το ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ σε ΚΑΘΕ επανάληψη, ΠΡΙΝ
        // ξαναδείξει το ΟΣΟ — αντιστοιχεί στο πέρασμα από το κλείσιμο του
        // βρόχου πριν επιστρέψει στον έλεγχο (ίδιος λόγος με το ΓΙΑ, βλ.
        // `executeFor`).
        if (stmt.endLine !== null) {
          this.onStatementExecuting?.(stmt.endLine);
          await this.waitDelay();
        }
        this.onStatementExecuting?.(stmt.line);
        // Ίδιος λόγος με το ΓΙΑ (βλ. `executeFor`) — χωρίς αυτό, το highlight
        // προλαβαίνει να αντικατασταθεί πριν προλάβει ο browser να το
        // ζωγραφίσει (no-op εκτός "Αργής εκτέλεσης").
        await this.waitDelay();
      }
    });
  }

  private async executeRepeatUntil(stmt: RepeatUntilStatement): Promise<void> {
    await this.withInferredLoopBound(stmt.condition, 'until', async () => {
      // Η ΠΡΩΤΗ φορά υπογραμμίζεται ήδη από το `executeStatement` (γενικός
      // dispatcher) — από τη 2η επανάληψη και μετά ξαναδείχνουμε ΚΑΙ τη
      // γραμμή του ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ σε κάθε επανάληψη (ίδιος λόγος με
      // ΓΙΑ/ΟΣΟ, βλ. `executeFor`), ΕΠΙΠΛΕΟΝ από το ΜΕΧΡΙΣ_ΟΤΟΥ που ήδη
      // ξαναδείχνεται.
      let isFirstIteration = true;
      for (;;) {
        if (isFirstIteration) isFirstIteration = false;
        else {
          this.onStatementExecuting?.(stmt.line);
          await this.waitDelay();
        }
        await this.executeStatements(stmt.body);
        // Ξαναδείχνει τη γραμμή του ΜΕΧΡΙΣ_ΟΤΟΥ σε ΚΑΘΕ έλεγχο — ίδιος
        // λόγος με το ΓΙΑ/ΟΣΟ.
        this.onStatementExecuting?.(stmt.condition.line);
        await this.waitDelay();
        const conditionResult = await this.evaluateBooleanCondition(stmt.condition, 'τη συνθήκη του ΜΕΧΡΙΣ_ΟΤΟΥ');
        // Βλ. `VariablesListener`/`lastCondition` (στήλη "Συνθήκη" στο
        // web UI) — σε αντίθεση με το ΓΙΑ/ΟΣΟ (που ελέγχουν ΠΡΙΝ το σώμα,
        // οπότε το "επόμενο reportVariables" ταιριάζει φυσικά), το
        // ΜΕΧΡΙΣ_ΟΤΟΥ ελέγχει ΜΕΤΑ το σώμα — αν περιμέναμε την επόμενη
        // αναφορά, θα "κολλούσε" στις τιμές της ΕΠΟΜΕΝΗΣ επανάληψης, όχι σε
        // αυτές που μόλις ελέγχθηκαν. Ρητή αναφορά ΕΔΩ, ώστε η στήλη
        // "Συνθήκη" να ταιριάζει με τις τιμές που πράγματι μόλις ελέγχθηκαν.
        this.lastCondition = { result: conditionResult, text: printExpression(stmt.condition, this.dialect) };
        this.reportVariables();
        if (conditionResult) break;
      }
    });
  }

  // --- Εκφράσεις -------------------------------------------------------------

  private async evaluateExpression(expr: Expression): Promise<RuntimeValue> {
    switch (expr.kind) {
      case 'NumberLiteral':
      case 'StringLiteral':
      case 'BooleanLiteral':
        return expr.value;
      case 'Identifier':
        return this.lookupVariable(expr.name, expr.line, expr.column);
      case 'ArrayAccess':
        return this.evaluateArrayAccess(expr);
      case 'FunctionCall':
        return this.evaluateFunctionCall(expr);
      case 'UnaryExpression':
        return this.evaluateUnary(expr);
      case 'BinaryExpression':
        return this.evaluateBinary(expr);
    }
  }

  private lookupVariable(name: string, line: number, column: number): RuntimeValue {
    const value = this.env.get(name);
    if (value === undefined) {
      // Ξεχωριστό μήνυμα για "ποτέ δεν δηλώθηκε" (μόνο ΓΛΩΣΣΑ, βλ.
      // `requireDeclaredInGlossa`) έναντι "δηλώθηκε αλλά δεν πήρε ακόμα τιμή".
      this.requireDeclaredInGlossa(name, line, column);
      throw new RuntimeError(`Η μεταβλητή '${name}' δεν έχει αρχικοποιηθεί`, line, column);
    }
    return value;
  }

  private async evaluateUnary(expr: UnaryExpression): Promise<RuntimeValue> {
    if (expr.operator === '-') {
      return -(await this.evaluateNumericOperand(expr.operand, "το μοναδιαίο '-'"));
    }
    return !(await this.evaluateBooleanOperand(expr.operand, "το 'ΟΧΙ'"));
  }

  private async evaluateBinary(expr: BinaryExpression): Promise<RuntimeValue> {
    // ΚΑΙ/Η βραχυκυκλώνουν — δεν αξιολογείται το δεξί μέλος αν περιττεύει.
    if (expr.operator === 'ΚΑΙ') {
      const left = await this.evaluateBooleanOperand(expr.left, "το 'ΚΑΙ'");
      return left ? this.evaluateBooleanOperand(expr.right, "το 'ΚΑΙ'") : false;
    }
    if (expr.operator === 'Η') {
      const left = await this.evaluateBooleanOperand(expr.left, "το 'Η'");
      return left ? true : this.evaluateBooleanOperand(expr.right, "το 'Η'");
    }

    // Ισότητα/ανισότητα δουλεύουν σε οποιονδήποτε τύπο.
    if (expr.operator === '=' || expr.operator === '<>') {
      const left = await this.evaluateExpression(expr.left);
      const right = await this.evaluateExpression(expr.right);
      return expr.operator === '=' ? left === right : left !== right;
    }

    // </<=/>/>= δουλεύουν σε δύο αριθμούς (αριθμητική σύγκριση) Η δύο
    // ΧΑΡΑΚΤΗΡΕΣ (λεξικογραφική σύγκριση, όπως σε λεξικό) — όχι σε ΛΟΓΙΚΕΣ
    // τιμές, και όχι ανάμεσα σε διαφορετικούς τύπους. Ξεχωριστό branch από
    // τους αριθμητικούς τελεστές παρακάτω, ώστε να ΜΗΝ αναγκάζονται και οι
    // δύο πλευρές σε αριθμό (βλ. `requireComparableOperands`).
    if (expr.operator === '<' || expr.operator === '<=' || expr.operator === '>' || expr.operator === '>=') {
      const left = await this.evaluateExpression(expr.left);
      const right = await this.evaluateExpression(expr.right);
      this.requireComparableOperands(left, right, expr);
      switch (expr.operator) {
        case '<':
          return left < right;
        case '<=':
          return left <= right;
        case '>':
          return left > right;
        case '>=':
          return left >= right;
      }
    }

    const left = await this.evaluateNumericOperand(expr.left, `τον τελεστή '${expr.operator}'`);
    const right = await this.evaluateNumericOperand(expr.right, `τον τελεστή '${expr.operator}'`);

    switch (expr.operator) {
      case '+':
        return left + right;
      case '-':
        return left - right;
      case '*':
        return left * right;
      case '/':
        this.requireNonZeroDivisor(right, expr.right);
        return left / right;
      case '^':
        return Math.pow(left, right);
      case 'DIV':
        this.requireIntegerDivModOperand(left, expr.left, 'DIV');
        this.requireIntegerDivModOperand(right, expr.right, 'DIV');
        this.requireNonZeroDivisor(right, expr.right);
        return Math.trunc(left / right);
      case 'MOD':
        this.requireIntegerDivModOperand(left, expr.left, 'MOD');
        this.requireIntegerDivModOperand(right, expr.right, 'MOD');
        this.requireNonZeroDivisor(right, expr.right);
        return left % right;
      default:
        throw new Error(`Εσωτερικό σφάλμα: μη αναμενόμενος τελεστής ${String(expr.operator)}`);
    }
  }

  /**
   * Οι τελεστές </<=/>/>= δουλεύουν είτε σε δύο αριθμούς είτε σε δύο
   * ΧΑΡΑΚΤΗΡΕΣ (λεξικογραφική σύγκριση) — όχι σε ΛΟΓΙΚΕΣ τιμές, και όχι
   * ανάμεσα σε διαφορετικούς τύπους (π.χ. αριθμό με ΧΑΡΑΚΤΗΡΕΣ).
   */
  private requireComparableOperands(left: RuntimeValue, right: RuntimeValue, expr: BinaryExpression): void {
    const bothNumbers = typeof left === 'number' && typeof right === 'number';
    const bothStrings = typeof left === 'string' && typeof right === 'string';
    if (bothNumbers || bothStrings) return;
    throw new RuntimeError(
      `Ο τελεστής '${expr.operator}' χρειάζεται δύο αριθμούς ή δύο ΧΑΡΑΚΤΗΡΕΣ — βρέθηκε ${describeRuntimeValue(left)} και ${describeRuntimeValue(right)}`,
      expr.line,
      expr.column,
    );
  }

  /** Το DIV/MOD (ακέραια διαίρεση) ορίζεται μόνο για μη αρνητικές ΑΚΕΡΑΙΕΣ τιμές (βλ. βιβλίο) — ελέγχει την ΤΙΜΗ, όχι τον δηλωμένο τύπο (μια ΠΡΑΓΜΑΤΙΚΗ με τιμή 4.0 περνάει κανονικά). */
  private requireIntegerDivModOperand(value: number, node: Expression, operator: 'DIV' | 'MOD'): void {
    if (!Number.isInteger(value)) {
      throw new RuntimeError(`Το ${operator} δέχεται μόνο ακέραιες τιμές, βρέθηκε ${describeRuntimeValue(value)}`, node.line, node.column);
    }
    if (value < 0) {
      throw new RuntimeError(`Το ${operator} δέχεται μόνο μη αρνητικές τιμές, βρέθηκε ${describeRuntimeValue(value)}`, node.line, node.column);
    }
  }

  private async evaluateBooleanCondition(expr: Expression, context: string): Promise<boolean> {
    return this.evaluateBooleanOperand(expr, context);
  }

  private async evaluateNumericOperand(expr: Expression, context: string): Promise<number> {
    const value = await this.evaluateExpression(expr);
    if (typeof value !== 'number') {
      throw new RuntimeError(
        `Αναμενόταν αριθμός για ${context}, βρέθηκε ${describeRuntimeValue(value)}`,
        expr.line,
        expr.column,
      );
    }
    return value;
  }

  private async evaluateBooleanOperand(expr: Expression, context: string): Promise<boolean> {
    const value = await this.evaluateExpression(expr);
    if (typeof value !== 'boolean') {
      throw new RuntimeError(
        `Αναμενόταν λογική τιμή για ${context}, βρέθηκε ${describeRuntimeValue(value)}`,
        expr.line,
        expr.column,
      );
    }
    return value;
  }

  private requireNonZeroDivisor(value: number, node: Expression): void {
    if (value === 0) {
      throw new RuntimeError('Διαίρεση με το μηδέν', node.line, node.column);
    }
  }

  private async evaluateFunctionCall(expr: FunctionCall): Promise<RuntimeValue> {
    const name = expr.callee.name;
    const builtin = BUILTIN_MATH_FUNCTIONS[normalizeKeywordText(name)];
    if (builtin !== undefined) {
      if (expr.args.length !== 1) {
        throw new RuntimeError(`Η συνάρτηση '${name}' περιμένει ακριβώς 1 όρισμα`, expr.line, expr.column);
      }
      const arg = await this.evaluateNumericOperand(expr.args[0]!, `το όρισμα της '${name}'`);
      const normalized = normalizeKeywordText(name);
      if (normalized === 'Τ_Ρ' && arg < 0) {
        throw new RuntimeError(`Η '${name}' δέχεται μόνο μη αρνητικό όρισμα`, expr.line, expr.column);
      }
      if (normalized === 'ΛΟΓ' && arg <= 0) {
        throw new RuntimeError(`Η '${name}' δέχεται μόνο θετικό όρισμα`, expr.line, expr.column);
      }
      return builtin(arg);
    }

    const sub = this.subprograms.get(name);
    if (sub === undefined) {
      throw new RuntimeError(`Η κλήση συνάρτησης '${name}' δεν υποστηρίζεται ακόμα`, expr.line, expr.column);
    }
    if (sub.kind === 'ProcedureDeclaration') {
      throw new RuntimeError(
        `Το '${name}' είναι διαδικασία, όχι συνάρτηση — καλείται με ΚΑΛΕΣΕ, όχι μέσα σε έκφραση`,
        expr.line,
        expr.column,
      );
    }
    if (expr.args.length !== sub.parameters.length) {
      throw new RuntimeError(
        `Η συνάρτηση '${name}' περιμένει ${sub.parameters.length} ορίσματα, δόθηκαν ${expr.args.length}`,
        expr.line,
        expr.column,
      );
    }
    const localEnv = await this.callSubprogram(sub, expr.args);
    if (!localEnv.has(sub.name)) {
      throw new RuntimeError(
        `Η συνάρτηση '${name}' δεν επέστρεψε τιμή (λείπει η εντολή "${name} ← έκφραση")`,
        expr.line,
        expr.column,
      );
    }
    return localEnv.get(sub.name)!;
  }

  private async executeCallStatement(stmt: CallStatement): Promise<void> {
    const name = stmt.callee.name;
    const sub = this.subprograms.get(name);
    if (sub === undefined) {
      throw new RuntimeError(`Η διαδικασία '${name}' δεν έχει οριστεί`, stmt.line, stmt.column);
    }
    if (sub.kind === 'FunctionDeclaration') {
      throw new RuntimeError(
        `Το '${name}' είναι συνάρτηση, όχι διαδικασία — καλείται μέσα σε έκφραση, όχι με ΚΑΛΕΣΕ`,
        stmt.line,
        stmt.column,
      );
    }
    if (this.insideFunction) {
      throw new RuntimeError('Μια ΣΥΝΑΡΤΗΣΗ δεν μπορεί να καλέσει ΔΙΑΔΙΚΑΣΙΑ', stmt.line, stmt.column);
    }
    if (stmt.args.length !== sub.parameters.length) {
      throw new RuntimeError(
        `Η διαδικασία '${name}' περιμένει ${sub.parameters.length} παραμέτρους, δόθηκαν ${stmt.args.length}`,
        stmt.line,
        stmt.column,
      );
    }
    await this.callSubprogram(sub, stmt.args);
  }

  /**
   * Καλεί ένα υποπρόγραμμα (ΔΙΑΔΙΚΑΣΙΑ ή ΣΥΝΑΡΤΗΣΗ) με "copy-in/copy-out"
   * σημασιολογία παραμέτρων — ΑΚΡΙΒΩΣ όπως το ιχνηλατεί το σχολικό βιβλίο:
   * (1) κάθε πραγματική παράμετρος αξιολογείται ΣΤΟ scope του καλούντος και
   *     αντιγράφεται ΜΙΑ ΦΟΡΑ στην τυπική παράμετρο· μια μη αρχικοποιημένη
   *     πραγματική αφήνει την τυπική ΕΠΙΣΗΣ χωρίς τιμή (όχι σφάλμα εδώ).
   * (2) το σώμα εκτελείται σε ΕΝΤΕΛΩΣ τοπικό Environment (καμία πρόσβαση σε
   *     μεταβλητές του καλούντος εκτός από τις παραμέτρους — αυστηρά τοπική
   *     εμβέλεια).
   * (3) ΜΟΝΟ για ΔΙΑΔΙΚΑΣΙΑ: η ΤΕΛΙΚΗ τιμή κάθε τυπικής παραμέτρου
   *     αντιγράφεται πίσω στην πραγματική (αν είναι lvalue) — ΜΙΑ ΦΟΡΑ, στο
   *     τέλος, όχι ζωντανή αναφορά καθ' όλη τη διάρκεια. Οι παράμετροι μιας
   *     ΣΥΝΑΡΤΗΣΗΣ είναι ΜΟΝΟ είσοδος (βλ. βιβλίο).
   * Επιστρέφει το τοπικό Environment (μετά την εκτέλεση) — ο καλών διαβάζει
   * ό,τι χρειάζεται από αυτό (π.χ. `evaluateFunctionCall` διαβάζει το όνομα
   * της συνάρτησης για την επιστρεφόμενη τιμή).
   */
  private async callSubprogram(sub: Subprogram, argExprs: readonly Expression[]): Promise<Environment> {
    const callerEnv = this.env;

    const argValues: (RuntimeValue | undefined)[] = [];
    for (const argExpr of argExprs) {
      if (argExpr.kind === 'Identifier' || argExpr.kind === 'ArrayAccess') {
        try {
          argValues.push(await this.readLValue(argExpr));
        } catch (e) {
          if (!(e instanceof RuntimeError)) throw e;
          argValues.push(undefined); // μη αρχικοποιημένη πραγματική -- η τυπική ξεκινάει ΕΠΙΣΗΣ χωρίς τιμή (βλ. βιβλίο)
        }
      } else {
        argValues.push(await this.evaluateExpression(argExpr));
      }
    }

    const localEnv = new Environment();
    sub.parameters.forEach((paramName, i) => {
      const value = argValues[i];
      if (value !== undefined) {
        const argExpr = argExprs[i]!;
        this.requireMatchingParameterKind(
          paramName,
          this.findParameterDeclarationInfo(sub.declarations, paramName),
          value,
          argExpr.line,
          argExpr.column,
        );
        localEnv.define(paramName, value);
      }
    });
    if (sub.kind === 'FunctionDeclaration') {
      localEnv.declareType(sub.name, RETURN_TYPE_TO_DECLARED_TYPE[sub.returnType]);
    }

    const wasInsideFunction = this.insideFunction;
    this.insideFunction = wasInsideFunction || sub.kind === 'FunctionDeclaration';
    const previousScope = this.currentScope;
    this.currentScope = sub.kind === 'FunctionDeclaration' ? { kind: 'function', name: sub.name } : { kind: 'procedure', name: sub.name };
    this.env = localEnv;
    // Ξεχωριστό Map ΓΙΑ-ορίων για το υποπρόγραμμα — αυστηρά τοπική εμβέλεια,
    // δεν πρέπει ένας ΓΙΑ του καλούντος να επηρεάσει το «μάντεμα» μεγέθους
    // πίνακα μέσα στο υποπρόγραμμα (βλ. `inferArrayDimensionsFromLoopContext`).
    const callerForLoopBounds = this.activeForLoopBounds;
    this.activeForLoopBounds = new Map();
    // Ξεχωριστό Set κλειδωμένων μετρητών ΓΙΑ για το υποπρόγραμμα — ίδιο
    // σκεπτικό με `activeForLoopBounds` (αυστηρά τοπική εμβέλεια).
    const callerLockedForCounters = this.lockedForCounters;
    this.lockedForCounters = new Set();
    // "Κλήση διαδικασίας/συνάρτησης" (βλ. πίνακας τιμών βιβλίου) — κατάσταση
    // ΑΜΕΣΩΣ μετά το copy-in, ΠΡΙΝ εκτελεστεί οποιαδήποτε εντολή του σώματος.
    this.reportVariables();
    try {
      // Κάθε παράμετρος πρέπει να έχει ρητή δήλωση στο ΜΕΤΑΒΛΗΤΕΣ του ΙΔΙΟΥ
      // του υποπρογράμματος (σύμβαση βιβλίου, βλ. `findUndeclaredGlossaReference`)
      // — δίχτυ ασφαλείας εδώ, το κανονικό σημείο ελέγχου είναι το στατικό.
      // Το μέγεθος ενός πίνακα-παραμέτρου πρέπει να ταιριάζει ΑΚΡΙΒΩΣ με το
      // δηλωμένο μέγεθος της τυπικής (βλ. `requireMatchingArraySize`) —
      // ΠΡΙΝ από το `registerDeclarations`, ώστε το σφάλμα να αναφέρεται
      // στο σημείο κλήσης, όχι κάπου βαθιά μέσα στο σώμα.
      for (let i = 0; i < sub.parameters.length; i++) {
        const paramName = sub.parameters[i]!;
        const info = this.findParameterDeclarationInfo(sub.declarations, paramName);
        if (info === undefined) {
          throw new RuntimeError(`Η παράμετρος '${paramName}' δεν έχει δηλωθεί στο ΜΕΤΑΒΛΗΤΕΣ`, sub.line, sub.column);
        }
        const value = localEnv.get(paramName);
        if (value === undefined) continue;
        if (info.isArray) {
          const argExpr = argExprs[i]!;
          await this.requireMatchingArraySize(paramName, info.dimensionExprs, value, argExpr.line, argExpr.column);
        }
      }
      await this.registerDeclarations(sub.declarations, sub.kind === 'FunctionDeclaration' ? sub.name : undefined);
      await this.executeStatements(sub.body);
      // Δείχνει ΚΑΙ το ΤΕΛΟΣ_ΔΙΑΔΙΚΑΣΙΑΣ/ΤΕΛΟΣ_ΣΥΝΑΡΤΗΣΗΣ πριν επιστρέψει στον
      // καλούντα — ίδιο σκεπτικό με το ΤΕΛΟΣ_ΑΝ (βλ. `executeIf`).
      if (sub.endLine !== null) {
        this.onStatementExecuting?.(sub.endLine);
        await this.waitDelay();
      }
    } finally {
      this.env = callerEnv;
      this.insideFunction = wasInsideFunction;
      this.currentScope = previousScope;
      this.activeForLoopBounds = callerForLoopBounds;
      this.lockedForCounters = callerLockedForCounters;
    }

    if (sub.kind === 'ProcedureDeclaration') {
      // Βλ. `executeStatements`/`reportSequence` — το copy-out παρακάτω τρέχει
      // ΑΦΟΥ το `this.env`/`this.currentScope` έχουν ήδη επιστρέψει στον
      // καλούντα (βλ. `finally` παραπάνω), οπότε μια αλλαγή εδώ ΔΕΝ αναφέρεται
      // από πουθενά αλλού· χρειάζεται δική της, ρητή αναφορά (μόνο αν όντως
      // άλλαξε κάτι — αλλιώς θα ήταν η ίδια διπλότυπη γραμμή που μόλις
      // διορθώθηκε στο `executeStatements`).
      let copiedOutAnything = false;
      for (let i = 0; i < sub.parameters.length; i++) {
        const paramName = sub.parameters[i]!;
        const argExpr = argExprs[i]!;
        const finalValue = localEnv.get(paramName);
        // Οι πίνακες είναι ήδη κοινόχρηστοι by-reference (καμία deep copy στο
        // copy-in, βλ. πιο πάνω) — η μεταβολή τους μέσα στο υποπρόγραμμα
        // φαίνεται ΗΔΗ στον καλούντα, δεν χρειάζεται ρητό copy-out. Δεν
        // επιτρέπεται κιόλας: θα σκόνταφτε στο `requireNotWholeArrayAssignment`.
        if (
          (argExpr.kind === 'Identifier' || argExpr.kind === 'ArrayAccess') &&
          finalValue !== undefined &&
          !(finalValue instanceof RuntimeArray)
        ) {
          await this.assignToLValue(argExpr, finalValue);
          copiedOutAnything = true;
        }
      }
      if (copiedOutAnything) this.reportVariables();
    }

    return localEnv;
  }
}

export async function interpret(
  program: Program,
  io: InterpreterIO,
  signal?: AbortSignal,
  onVariablesChanged?: VariablesListener,
  onStatementExecuting?: StatementListener,
  delayMs?: number,
  maxSteps?: number,
  onBreakpoint?: BreakpointHook,
): Promise<InterpretResult> {
  return new Interpreter(io).run(program, signal, onVariablesChanged, onStatementExecuting, delayMs, maxSteps, onBreakpoint);
}

/** "Τμήμα αλγορίθμου" — βλ. `Interpreter.runStatements`. */
export async function interpretStatements(
  statements: readonly Statement[],
  subprograms: readonly Subprogram[],
  io: InterpreterIO,
  signal?: AbortSignal,
  onVariablesChanged?: VariablesListener,
  onStatementExecuting?: StatementListener,
  delayMs?: number,
  maxSteps?: number,
  onBreakpoint?: BreakpointHook,
): Promise<InterpretResult> {
  return new Interpreter(io).runStatements(
    statements,
    subprograms,
    signal,
    onVariablesChanged,
    onStatementExecuting,
    delayMs,
    maxSteps,
    onBreakpoint,
  );
}
